<script setup>
// [TODO] This componet is too big and bulky to be in the same file, we can consider splitting this into multiple
// composables and components, useVirtualChatList, useChatlistFilters
import { requestConclusionResult } from 'dashboard/composables/useConclusionResultPrompt';
import {
  ref,
  unref,
  provide,
  computed,
  watch,
  nextTick,
  onMounted,
  onUnmounted,
  defineEmits,
} from 'vue';
import { useStore } from 'vuex';
import { vOnClickOutside } from '@vueuse/components';
import { useRoute, useRouter } from 'vue-router';
import SearchAPI from 'dashboard/api/search';
import AceleresTeamPresenceAPI from 'dashboard/api/aceleresTeamPresence';
import {
  useMapGetter,
  useFunctionGetter,
} from 'dashboard/composables/store.js';

import { Virtualizer } from 'virtua/vue';
import ChatListHeader from './ChatListHeader.vue';
import StarredMessagesPanel from './StarredMessagesPanel.vue';
import ConversationFilter from 'next/filter/ConversationFilter.vue';
import QuickConversationFilter from 'next/filter/QuickConversationFilter.vue';
import AceleresDropdownFilter from './AceleresDropdownFilter.vue';
import SaveCustomView from 'next/filter/SaveCustomView.vue';
import ChatTypeTabs from './widgets/ChatTypeTabs.vue';
import ConversationItem from './ConversationItem.vue';
import AceleresNewConversationPanel from './AceleresNewConversationPanel.vue';
import AclConversationDeleteModal from './widgets/conversation/AclConversationDeleteModal.vue';
import AclPinLimitModal from './widgets/conversation/AclPinLimitModal.vue';
import FunnelConfigurationModal from './widgets/conversation/FunnelConfigurationModal.vue';
import FunnelStageConfigurationModal from './widgets/conversation/FunnelStageConfigurationModal.vue';
import FunnelAssistantModal from './widgets/conversation/FunnelAssistantModal.vue';
import AclChatListEmptyState from './widgets/conversation/AclChatListEmptyState.vue';
import { useAclListResize } from 'dashboard/composables/useAclListResize';
import { useConversationListStartMargin } from 'dashboard/composables/useConversationListStartMargin';
import { useAclQuickSearch } from 'dashboard/composables/useAclQuickSearch';
import { inboxDisplayName } from 'dashboard/helper/inboxDisplayName';
import AceleresStartConversationFlow from './AceleresStartConversationFlow.vue';
import DeleteCustomViews from 'dashboard/routes/dashboard/customviews/DeleteCustomViews.vue';
import ConversationBulkActions from './widgets/conversation/conversationBulkActions/Index.vue';
import AclSelectionToolbar from './widgets/conversation/AclSelectionToolbar.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import IntersectionObserver from 'dashboard/components/IntersectionObserver.vue';
import ConversationResolveAttributesModal from 'dashboard/components-next/ConversationWorkflow/ConversationResolveAttributesModal.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import SearchPill from 'dashboard/components-next/search-pill/SearchPill.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { useChatListKeyboardEvents } from 'dashboard/composables/chatlist/useChatListKeyboardEvents';
import { useBulkActions } from 'dashboard/composables/chatlist/useBulkActions';
import { useFilter } from 'shared/composables/useFilter';
import { useTrack } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import {
  useCamelCase,
  useSnakeCase,
} from 'dashboard/composables/useTransformKeys';
import { useEmitter } from 'dashboard/composables/emitter';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';
import { useFunilStages } from 'dashboard/composables/useFunilStages';

import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_TYPE } from 'shared/constants/messages';
import { isAceleresIndividualOwn } from 'dashboard/constants/aceleresConnectionTypes';
import {
  isAceleresConversationStarted,
  isAceleresPersonalConversationForUser,
} from 'dashboard/helper/aceleresConversationState';
import { isGroupConversation } from 'dashboard/components-next/message/groupParticipant';
import { filterServerValidatedConversations } from 'dashboard/helper/conversationPrefetchCache';
import { waitForZoraxyCriticalData } from 'dashboard/helper/zoraxyStartupLoading';

import wootConstants from 'dashboard/constants/globals';
import advancedFilterOptions from './widgets/conversation/advancedFilterItems';
import filterQueryGenerator from '../helper/filterQueryGenerator.js';
import { useLocalizedLists } from 'shared/composables/useLocalizedLists';
import { generateValuesForEditCustomViews } from 'dashboard/helper/customViewsHelper';
import { conversationListPageURL } from '../helper/URLHelper';
import {
  isOnMentionsView,
  isOnParticipatingView,
  isOnUnattendedView,
} from '../store/modules/conversations/helpers/actionHelpers';
import {
  getUserPermissions,
  filterItemsByPermission,
} from 'dashboard/helper/permissionsHelper.js';
import { matchesFilters } from '../store/modules/conversations/helpers/filterHelpers';
import { CONVERSATION_EVENTS } from '../helper/AnalyticsHelper/events';
import { ASSIGNEE_TYPE_TAB_PERMISSIONS } from 'dashboard/constants/permissions.js';
import {
  shouldRunChatListAutoRefresh,
  CHAT_LIST_AUTO_REFRESH_INTERVAL,
} from 'dashboard/helper/chatListAutoRefresh';
import { resolveConversationListInboxId } from 'dashboard/helper/conversationListFilters';

const props = defineProps({
  conversationInbox: { type: [String, Number], default: 0 },
  teamId: { type: [String, Number], default: 0 },
  label: { type: String, default: '' },
  conversationType: { type: String, default: '' },
  foldersId: { type: [String, Number], default: 0 },
  showConversationList: { default: true, type: Boolean },
  isOnExpandedLayout: { default: false, type: Boolean },
});

const emit = defineEmits(['conversationLoad']);
const { uiSettings } = useUISettings();
const { t } = useI18n();
const router = useRouter();
const route = useRoute();
// Aceleres: arquivadas são um estado interno do histórico, mas a interface
// exibe concluídas e arquivadas juntas na única aba "Concluídos".
const shouldMergeArchivedIntoConcluded = ref(true);

// ─── Aceleres: quick search com dropdown live (WhatsApp Web-style) ─────────
// Aceleres 2026-08-28: a busca rapida virou composable.
// `isPersonalBoxActive` e passado pelo pai de proposito: e ele que tem a rota,
// e esse parametro decide o `aceleresBox: pessoais` da consulta. Sem ele a busca
// deixa de restringir a caixa Pessoais e devolve conversa de outro vendedor.
const {
  quickSearch,
  quickSearchOpen,
  quickSearchLoading,
  quickSearchError,
  quickSearchIsPhone,
  quickSearchNoResults,
  quickSearchContacts,
  quickSearchConversations,
  isSearchActive,
  onQuickSearchInput,
  onQuickSearchEnter,
  closeQuickSearch,
} = useAclQuickSearch({
  isPersonalBoxActive: () =>
    normalizeAceleresBoxQuery(route.query[ACELERES_BOX_QUERY_PARAM]) ===
    'pessoais',
  onReset: () => startConversationFlowRef.value?.reset(),
});

const accountIdForQuickSearch = useMapGetter('getCurrentAccountId');
function openConvFromQuick(conv) {
  const accId = accountIdForQuickSearch.value;
  router.push(`/app/accounts/${accId}/conversations/${conv.id}`);
}
function openContactFromQuick(contact) {
  const accId = accountIdForQuickSearch.value;
  router.push(`/app/accounts/${accId}/contacts/${contact.id}`);
}

function isQuickSearchConversationActive(conversationId) {
  return Number(conversationId) === activeQuickSearchConversationId.value;
}

const START_PHONE_QUERY_PARAM = 'start_phone';
const NEW_CONVERSATION_QUERY_PARAM = 'new_conversation';
const ACELERES_BOX_QUERY_PARAM = 'aceleres_box';

// Aceleres: fluxo compartilhado de nova conversa (seleção de instância,
// ownership e criação). O telefone chega do painel deslizante
// (AceleresNewConversationPanel) ou do quick-search.
const startConversationFlowRef = ref(null);
const isStartingConversation = computed(
  () => startConversationFlowRef.value?.isBusy || false
);
const showNewConversationPanel = ref(false);
const showNewContactInEmptyState = ref(false);
const showStarredMessages = ref(false);
const EMPTY_STATE_NEW_CONTACT_EVENT = 'acl:open-new-contact-in-empty-state';
const newConversationPanelTeleportTarget = computed(() =>
  showNewContactInEmptyState.value ? '.acl-vazio-chat' : null
);

function openStarredMessages() {
  closeQuickSearch();
  showNewConversationPanel.value = false;
  showStarredMessages.value = true;
}

function closeStarredMessages() {
  showStarredMessages.value = false;
}

function waitForStarredMessage(messageId, attempts = 180) {
  return new Promise(resolve => {
    const findMessage = remainingAttempts => {
      if (document.getElementById(`message${messageId}`)) {
        resolve(true);
        return;
      }
      if (!remainingAttempts) {
        resolve(false);
        return;
      }
      window.requestAnimationFrame(() => findMessage(remainingAttempts - 1));
    };

    findMessage(attempts);
  });
}

async function focusStarredMessage(conversationId, messageId) {
  if (!document.getElementById(`message${messageId}`)) {
    await store.dispatch('fetchPreviousMessages', {
      conversationId,
      after: messageId - 1,
      before: messageId + 1,
      limit: 3,
    });
  }

  await nextTick();
  if (await waitForStarredMessage(messageId)) {
    emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId });
  }
}

async function openStarredMessage(message) {
  const conversationId = Number(
    message.conversation_id || message.conversation?.id
  );
  const messageId = Number(message.id);
  if (!conversationId || !messageId) return;

  showStarredMessages.value = false;

  if (conversationId === activeQuickSearchConversationId.value) {
    await focusStarredMessage(conversationId, messageId);
    return;
  }

  await router.push({
    path: `/app/accounts/${accountIdForQuickSearch.value}/conversations/${conversationId}`,
    query: { messageId },
  });
  await store.dispatch('getConversation', conversationId);
  await focusStarredMessage(conversationId, messageId);
}

function openHeaderNewConversation() {
  showNewContactInEmptyState.value = false;
  showNewConversationPanel.value = true;
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, true);
}

function closeHeaderNewConversation() {
  showNewConversationPanel.value = false;
  showNewContactInEmptyState.value = false;
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, false);
}

function openNewContactFromEmptyState() {
  showNewContactInEmptyState.value = true;
  showNewConversationPanel.value = true;
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, true);
}

function startConversationFromPanel(phone) {
  closeHeaderNewConversation();
  openNewConversationForPhone(phone);
}

function openContactChatFromPanel(contact) {
  closeHeaderNewConversation();
  if (contact?.phone_number) {
    openNewConversationForPhone(contact.phone_number);
    return;
  }
  openContactFromQuick(contact);
}

function editContactFromPanel(contact) {
  closeHeaderNewConversation();
  openContactFromQuick(contact);
}

const currentUser = useMapGetter('getCurrentUser');

// Delega para o fluxo compartilhado (seleção de instância + ownership + criação
// da conversa), o mesmo usado pela página de Contatos.
async function openNewConversationForPhone(phone = null) {
  const q = (phone || quickSearch.value || '').trim();
  if (!q) return;
  // O deep link ?start_phone= dispara ainda durante o setup, antes de o fluxo
  // estar montado.
  if (!startConversationFlowRef.value) await nextTick();
  startConversationFlowRef.value?.startForPhone(q);
}

// O cartao de contato do chat pede "falar com este telefone" pelo bus: a bolha
// nao pode hospedar o fluxo (haveria uma copia por mensagem na tela) nem
// repetir as regras dele.
function openChatWithPhoneFromBus(payload) {
  const phone = typeof payload === 'string' ? payload : payload?.phone;
  if (phone) openNewConversationForPhone(phone);
}

const store = useStore();

const resolveAttributesModalRef = ref(null);
const conversationListRef = ref(null);
const virtualListRef = ref(null);
const virtualListStartMargin = useConversationListStartMargin(
  conversationListRef,
  virtualListRef
);

provide('contextMenuElementTarget', virtualListRef);

// Aceleres: caixas operacionais Novos/Meus/Outros/Pessoais;
// a abertura padrão é Novos, e Outros aparece para gestores e supervisores.
const aceleresDefaultAssigneeTab = 'novos';
const UNREAD_SUBFILTER_ALL = 'all';
const UNREAD_SUBFILTER_ONLY = 'unread';
const activeAssigneeTab = ref(aceleresDefaultAssigneeTab);
const unreadSubfiltersByTab = ref({
  novos: UNREAD_SUBFILTER_ALL,
  [wootConstants.ASSIGNEE_TYPE.ME]: UNREAD_SUBFILTER_ALL,
  outros: UNREAD_SUBFILTER_ALL,
  [wootConstants.ASSIGNEE_TYPE.PERSONAL]: UNREAD_SUBFILTER_ALL,
});
const aceleresUserPickedTab = ref(false);
const activeStatus = ref(wootConstants.STATUS_TYPE.OPEN);
const activeSortBy = ref(wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC);
const activeInboxFilter = ref(null);
const iaOnly = ref(false);
const qfPriority = ref(null);
const qfLabelSlug = ref(null);
const qfAgentId = ref(null);
const qfTeamId = ref(null);
const isSellerProfile = computed(
  () => currentUser.value?.aceleres_active_profile === 'vendedor'
);
const isManagerRole = computed(
  () =>
    !isSellerProfile.value &&
    store.getters.getCurrentRole === 'administrator'
);
const showAdvancedFilters = ref(false);
const aceleresFilterApplied = ref(false);
const aceleresFilterCount = ref(0);
const aceleresFilterMenuOpen = ref(false);

function getAceleresSessionValue(key, factory) {
  if (typeof window === 'undefined') {
    return factory();
  }

  if (!Reflect.has(window, key)) {
    Reflect.set(window, key, factory());
  }
  return Reflect.get(window, key);
}

// Aceleres: true quando o filtro aplicado inclui "Atendidos pela IA"
// (attribute_key atendido_ia). Alimenta o empty state contextual.
const appliedIaFilter = ref(false);
const aceleresDropdownFilterRef = ref(null);
// Aceleres: assinatura (filtros+pagina+tamanho da lista) do ultimo fetch
// disparado pelo sentinel — impede refetch identico em loop.
const aceleresLastLoadMoreKey = ref(null);
const aceleresConversationCache = getAceleresSessionValue(
  '__aceleresConversationTabCache:v3',
  () => new Map()
);
const aceleresCompleteConversationCacheKeys = getAceleresSessionValue(
  '__aceleresCompleteConversationTabCacheKeys:v3',
  () => new Set()
);
// Aceleres: quantas paginas ja foram carregadas em cada visao cacheada.
// Sem esse rastreio, restaurar o cache devolvia a lista inteira mas com a
// paginacao zerada -- o scroll seguinte pedia a pagina 1 de novo e o
// historico antigo nunca era alcancado.
const aceleresConversationCachePages = getAceleresSessionValue(
  '__aceleresConversationTabCachePages:v1',
  () => new Map()
);
// Aceleres 2026-09-10: Concluidos/Arquivadas passaram a usar cache TAMBEM.
//
// Antes essas abas eram as unicas proibidas de cachear (a lista muda fora do
// navegador -- o funil conclui conversa sozinho), entao toda entrada esperava
// o round-trip inteiro com o spinner na tela. A query da caixa nao e o
// problema: medida em homolog na conta com 268 concluidas, `concluidos` custa
// 58ms no ConversationFinder, praticamente o mesmo que `meus` (30ms). O que
// pesa e o request HTTP completo (~2s observados no production.log, com so
// 646ms de ActiveRecord).
//
// O snapshot agora pinta imediato e SEMPRE revalida: a chave de historico
// nunca entra em `aceleresCompleteConversationCacheKeys`, entao
// `fetchConversationsOnTab` cai no ramo `refreshConversationListInBackground`.
// O snapshot tambem nao vai para o sessionStorage (`persistAceleresConversationList`
// so persiste chave operacional) e vence em ACELERES_HISTORY_CACHE_TTL_MS --
// as duas travas que evitam mostrar historico velho de outra sessao.
//
// O TTL e de 5 min, nao de segundos, porque quem popula esta chave e o
// aquecimento de boot (ver prefetchAceleresStartupTabs): com uma validade
// curta o utilizador que abre Concluidos minutos depois de entrar pagaria o
// request na mesma, e o aquecimento nao serviria para nada. Uma lista de 5 min
// atras e aceitavel porque a revalidacao chega logo a seguir; a revalidacao
// tambem re-carimba o snapshot, entao a aba em uso nunca envelhece.
const ACELERES_HISTORY_CACHE_TTL_MS = 5 * 60 * 1000;
const aceleresHistoryCacheStamps = getAceleresSessionValue(
  '__aceleresHistoryConversationCacheStamps:v1',
  () => new Map()
);

function isFreshAceleresHistorySnapshot(cacheKey) {
  const stampedAt = aceleresHistoryCacheStamps.get(cacheKey);
  return (
    Number.isFinite(stampedAt) &&
    Date.now() - stampedAt < ACELERES_HISTORY_CACHE_TTL_MS
  );
}
const aceleresConversationCacheVersion = ref(0);
const ACELERES_CONVERSATION_CACHE_LIMIT = 32;
// v5: invalida snapshots anteriores a is_pinned/pinned_at serem persistidos.
const ACELERES_PERSISTED_LIST_CACHE_PREFIX = 'zoraxy:conversation-list:v5';
const ACELERES_PERSISTED_LIST_CACHE_TTL = 2 * 60 * 1000;
const ACELERES_PERSISTED_LIST_MAX_ITEMS = 50;
// 15s (era 60s): esta janela decide por quanto tempo um pedido NAO forcado de
// contadores e ignorado como "ja esta fresco". Com 60s, o badge da aba e o do
// icone Conversas (mesmo snapshot, SidebarGroupHeader) podiam segurar um numero
// vencido por ate um minuto quando a mudanca nao veio por evento otimista.
const ACELERES_STATS_CACHE_TTL = 15000;
const ACELERES_CONVERSATIONS_PER_PAGE = 25;
// Distância do fim da lista que já conta como "chegou ao fim" para pedir a
// próxima página; mesma ordem de grandeza do rootMargin do sentinel.
const ACELERES_LOAD_MORE_MARGIN = 150;
// Espaço extra (px) que o Virtualizer mantém montado acima e abaixo da área
// visível. O padrão da biblioteca é 200px -- menos de dois cartões: ao subir
// a lista, o trecho já carregado aparecia EM BRANCO até o próximo render, e
// no celular (CPU mais lenta) o buraco chegava à tela inteira. Medido em
// homologação: com 200px, buracos de 16 a 619px de 619 visíveis.
const ACELERES_VIRTUAL_BUFFER_PX = 1200;
const CONCLUDED_ASSIGNEE_TAB = 'concluidos';
const CONCLUDED_TAB_LABEL = 'Concluídos';
const ARCHIVED_ASSIGNEE_TAB = 'arquivadas';
const isHistoryTab = tab =>
  [CONCLUDED_ASSIGNEE_TAB, ARCHIVED_ASSIGNEE_TAB].includes(tab);
const ACELERES_TAB_BOXES = {
  novos: 'novos',
  [wootConstants.ASSIGNEE_TYPE.ME]: 'meus',
  outros: 'outros',
  [wootConstants.ASSIGNEE_TYPE.PERSONAL]: 'pessoais',
  [CONCLUDED_ASSIGNEE_TAB]: 'concluidos',
  [ARCHIVED_ASSIGNEE_TAB]: 'arquivadas',
};
const isOperationalAceleresTab = tab =>
  Object.hasOwn(ACELERES_TAB_BOXES, tab) && !isHistoryTab(tab);
let aceleresStartupPrefetchRequests = 0;
let aceleresCriticalMenuDataPromise = null;
// As caixas principais e as mensagens da primeira página são carregadas no
// boot. Assim, trocar de aba só troca a visão local já disponível.
const aceleresStartupTabsReady = ref(false);
let aceleresStartupTabsPromise = null;
// chatsOnView is to store the chats that are currently visible on the screen,
// which mirrors the conversationList.
const chatsOnView = ref([]);
// Evita exibir a lista anterior durante a troca de caixa/subfiltro. A chave
// garante que uma resposta atrasada não encerre a transição da visão atual.
const aceleresViewTransitioning = ref(false);
const aceleresAppendInFlight = ref(false);

// Aceleres: "Iniciar atendimento" e fruto de um clique, e a lista que vem logo
// depois nao pode ser servida por cache nenhum.
//
// O endpoint /conversations guarda a pagina renderizada por 30s
// (CONVERSATION_LIST_CACHE_TTL, mais 10s de race_condition_ttl) numa chave que
// so considera conta, usuario e query -- nao ha invalidacao em lugar nenhum do
// codigo. Quem acabou de assumir a conversa recebia, portanto, a caixa Meus
// como ela era ate 40s antes: SEM o atendimento recem-iniciado. O card sumia e
// so voltava quando o TTL vencia (relato de 25/08/2026, conversa 33 da conta
// 2569 -- "sumiu e depois de tipo um minuto apareceu de volta").
//
// O comentario do cache no controller assume que o ActionCable corrige o
// navegador. Ele ate emite (`attendance_started` esta em
// BROADCASTABLE_ADDITIONAL_ATTRIBUTES), mas a pagina cacheada chega DEPOIS e
// sobrescreve a correcao. Furar o cache uma vez, no clique, e o unico ponto em
// que o dado fresco e obrigatorio.
const aceleresForceServerListRefresh = ref(false);
const foldersQuery = ref({});
const showAddFoldersModal = ref(false);
const showDeleteFoldersModal = ref(false);
const showFunnelConfigurationModal = ref(false);
const showFunnelStageConfigurationModal = ref(false);
const showFunnelAssistantModal = ref(false);
const selectedStageForConfiguration = ref(null);
const openStageConfiguration = stage => {
  selectedStageForConfiguration.value = stage;
  showFunnelStageConfigurationModal.value = true;
};
// Limite de fixadas: `Conversations::PinConversationService::LIMIT` e 3.
const PINNED_CONVERSATION_LIMIT = 3;
const showPinLimitModal = ref(false);
const isContextMenuOpen = ref(false);
const appliedFilter = ref([]);
const activeAceleresFilterConditions = ref([]);
const advancedFilterTypes = ref(
  advancedFilterOptions.map(filter => ({
    ...filter,
    attributeName: t(`FILTER.ATTRIBUTES.${filter.attributeI18nKey}`),
  }))
);

const allConversationsList = useMapGetter('getAllConversations');
const conversationStatusSnapshot = computed(() =>
  (allConversationsList.value || [])
    .map(conversation => `${conversation?.id}:${conversation?.status}`)
    .join('|')
);
const chatLists = useMapGetter('getFilteredConversations');
const mineChatsList = useMapGetter('getMineChats');
const allChatList = useMapGetter('getAllStatusChats');
const unAssignedChatsList = useMapGetter('getUnAssignedChats');
const participatingChatsList = useMapGetter('getParticipatingChats');
const chatListLoading = useMapGetter('getChatListLoadingStatus');
const chatListHasError = useMapGetter('getChatListErrorStatus');
const activeInbox = useMapGetter('getSelectedInbox');
const currentChat = useMapGetter('getSelectedChat');
const conversationStats = useMapGetter('conversationStats/getStats');
const appliedFilters = useMapGetter('getAppliedConversationFiltersV2');
const folders = useMapGetter('customViews/getConversationCustomViews');
const agentList = useMapGetter('agents/getAgents');
const teamsList = useMapGetter('teams/getTeams');
const inboxesList = useMapGetter('inboxes/getInboxes');

watch(
  () => route.query[START_PHONE_QUERY_PARAM],
  async phoneQuery => {
    const phone = Array.isArray(phoneQuery) ? phoneQuery[0] : phoneQuery;
    if (!phone) return;

    if (!inboxesList.value?.length) {
      await store.dispatch('inboxes/get');
    }

    openNewConversationForPhone(phone);
    const nextQuery = { ...route.query };
    delete nextQuery[START_PHONE_QUERY_PARAM];
    router.replace({
      name: route.name,
      params: route.params,
      query: nextQuery,
    });
  },
  { immediate: true }
);

// Aceleres: o botao de nova conversa da barra lateral navega para ca com
// ?new_conversation=1 em vez de abrir o compositor nativo do Chatwoot, que
// criava a conversa sem selecao de instancia, sem checagem de lead ja
// caracterizado e sem marcar o atendimento como iniciado -- ou seja, ela
// nascia em "Novos". Vem por query, e nao por evento de barramento, porque
// quando o clique acontece a ChatList pode nem estar montada para escutar.
watch(
  () => route.query[NEW_CONVERSATION_QUERY_PARAM],
  requested => {
    if (!requested) return;

    openHeaderNewConversation();
    const nextQuery = { ...route.query };
    delete nextQuery[NEW_CONVERSATION_QUERY_PARAM];
    router.replace({
      name: route.name,
      params: route.params,
      query: nextQuery,
    });
  },
  { immediate: true }
);
const campaigns = useMapGetter('campaigns/getAllCampaigns');
const labels = useMapGetter('labels/getLabels');
const currentAccountId = useMapGetter('getCurrentAccountId');
const {
  funnels,
  stagesForFunnel,
  load: loadFunilStages,
  refresh: refreshFunilStages,
} = useFunilStages(currentAccountId);
const selectedFunnelId = ref('');
const funnelStorageKey = computed(
  () => `aceleres_last_funnel_${currentAccountId.value || 'default'}`
);

const restoreSelectedFunnel = () => {
  const available = funnels.value;
  if (available.length < 2) {
    selectedFunnelId.value = available[0]?.id || '';
    return;
  }

  let saved = '';
  try {
    saved = localStorage.getItem(funnelStorageKey.value) || '';
  } catch {
    saved = '';
  }

  selectedFunnelId.value = available.some(
    funnel => String(funnel.id) === String(saved)
  )
    ? saved
    : available[0].id;
};

const onSelectFunnel = funnelId => {
  if (!funnels.value.some(funnel => String(funnel.id) === String(funnelId))) {
    restoreSelectedFunnel();
    return;
  }

  selectedFunnelId.value = funnelId;
  try {
    localStorage.setItem(funnelStorageKey.value, String(funnelId));
  } catch {
    // A storage failure must not affect the conversation list.
  }
};

const isConversationInSelectedFunnel = conversation => {
  if (funnels.value.length < 2 || !selectedFunnelId.value) return true;

  const stages = stagesForFunnel(selectedFunnelId.value);
  // Sem metadados de etapas no contrato novo, manter a lista intacta é mais
  // seguro do que esconder conversas. O filtro só é aplicado quando o funil
  // informa seus identificadores estáveis.
  if (!stages.length) return true;

  const labels = new Set(
    Array.isArray(conversation?.labels) ? conversation.labels : []
  );
  return stages.some(stage => labels.has(stage.slug) || labels.has(stage.tag));
};

watch([currentAccountId, funnels], restoreSelectedFunnel, { immediate: true });
onMounted(loadFunilStages);
// We can't useFunctionGetter here since it needs to be called on setup?
const getTeamFn = useMapGetter('teams/getTeam');
const getConversationById = useMapGetter('getConversationById');
const activeQuickSearchConversationId = computed(() =>
  Number(currentChat.value?.id || route.params.conversation_id || 0)
);

const supervisedTeamIds = computed(() =>
  (teamsList.value || [])
    .filter(team => team.is_supervisor)
    .map(team => Number(team.id))
    .filter(id => !Number.isNaN(id))
);
const isSupervisorUser = computed(
  () => !isSellerProfile.value && supervisedTeamIds.value.length > 0
);
const isSellerVisibilityProfile = computed(
  () =>
    isSellerProfile.value ||
    (store.getters.getCurrentRole === 'agent' && !isSupervisorUser.value)
);
const currentUserTeamIds = computed(() => {
  const currentUserId = Number(currentUser.value?.id);
  const currentAgent = (agentList.value || []).find(
    agent => Number(agent.id) === currentUserId
  );
  const agentTeamIds = (currentAgent?.teams || []).map(team => Number(team.id));
  const userTeamIds = (currentUser.value?.teams || []).map(team => Number(team.id));
  const listedTeamIds = (teamsList.value || [])
    .filter(team => team?.is_member || team?.is_supervisor)
    .map(team => Number(team.id));

  return [...new Set([...agentTeamIds, ...userTeamIds, ...listedTeamIds])].filter(
    id => !Number.isNaN(id)
  );
});
const managerTeamIds = computed(() => {
  if (!isManagerRole.value) return [];

  const currentUserId = Number(currentUser.value?.id);
  const currentAgent = (agentList.value || []).find(
    agent => Number(agent.id) === currentUserId
  );
  const agentTeamIds = (currentAgent?.teams || []).map(team => Number(team.id));
  const userTeamIds = (currentUser.value?.teams || []).map(team => Number(team.id));

  return [...new Set([...agentTeamIds, ...userTeamIds])].filter(
    id => !Number.isNaN(id)
  );
});
const managerSellerIds = computed(() => {
  if (!isManagerRole.value) return [];

  const sellers = (agentList.value || []).filter(
    agent => agent.role === 'agent'
  );
  const teamIds = managerTeamIds.value;

  if (!teamIds.length) {
    return sellers.map(agent => Number(agent.id));
  }

  return sellers
    .filter(agent =>
      (agent.teams || []).some(team => teamIds.includes(Number(team.id)))
    )
    .map(agent => Number(agent.id));
});
// A aba "Outros" é uma visão de acompanhamento da equipe supervisionada.
// Gestor sem o papel de supervisor não deve receber essa caixa; usuários com
// os dois perfis continuam autorizados por isSupervisorUser.
const canSeeOthersBox = computed(() => isSupervisorUser.value);
// Espelha managerSellerIds, mas com base nas equipes de SUPERVISÃO
// (supervisedTeamIds), igual ao supervised_scope do backend
// (conversation_finder.rb) — "Outros" nunca deve usar as equipes de gestor.
const supervisedSellerIds = computed(() => {
  if (!isSupervisorUser.value) return [];

  const sellers = (agentList.value || []).filter(
    agent => agent.role === 'agent'
  );
  const teamIds = supervisedTeamIds.value;

  return sellers
    .filter(agent =>
      (agent.teams || []).some(team => teamIds.includes(Number(team.id)))
    )
    .map(agent => Number(agent.id));
});
const canSeeUnassignedLeads = computed(
  () => isManagerRole.value || isSupervisorUser.value
);
const ownedPersonalInboxIds = computed(() => {
  const currentUserId = Number(currentUser.value?.id);
  if (!currentUserId) {
    return [];
  }

  return (inboxesList.value || [])
    .filter(inbox => {
      const attrs = inbox.additional_attributes || {};
      return (
        isAceleresIndividualOwn(attrs.connection_type) &&
        Number(attrs.owner_chatwoot_user_id) === currentUserId
      );
    })
    .map(inbox => Number(inbox.id))
    .filter(id => !Number.isNaN(id));
});
// A aba Pessoais só existe quando o usuário possui uma inbox individual_own
// vinculada a ele. O estado de conexão não muda a existência da instância.
const shouldShowPersonalTab = computed(
  () => ownedPersonalInboxIds.value.length > 0
);

const hasPersonalConversationMarker = conversation => {
  const attrs =
    conversation?.additional_attributes ||
    conversation?.additionalAttributes ||
    {};
  const inboxId = Number(conversation?.inbox_id ?? conversation?.inboxId ?? 0);

  return (
    Number(
      attrs.personal_owner_chatwoot_user_id ||
        attrs.personalOwnerChatwootUserId ||
        0
    ) > 0 ||
    ownedPersonalInboxIds.value.includes(inboxId)
  );
};

// A regra mora em `aceleresConversationState` — o chat de atendimento precisa
// da mesma resposta para esconder o nome do vendedor na caixa Pessoais, e as
// duas telas não podem divergir sobre o que está em Pessoais.
const isPersonalConversationForCurrentUser = conversation =>
  isAceleresPersonalConversationForUser({
    conversation,
    currentUserId: currentUser.value?.id,
    ownsPersonalInbox: ownedPersonalInboxIds.value.includes(
      Number(conversation?.inbox_id ?? conversation?.inboxId ?? 0)
    ),
  });

useChatListKeyboardEvents(conversationListRef);
const {
  selectedConversations,
  selectedInboxes,
  selectConversation,
  deSelectConversation,
  selectAllConversations,
  resetBulkActions,
  isConversationSelected,
  onAssignAgent: bulkAssignAgent,
  onAssignLabels,
  onRemoveLabels,
  onAssignTeamsForBulk,
  onUpdateConversations,
} = useBulkActions();

const {
  initializeStatusAndAssigneeFilterToModal,
  initializeInboxTeamAndLabelFilterToModal,
} = useFilter({
  filteri18nKey: 'FILTER',
  attributeModel: 'conversation_attribute',
});

const { checkMissingAttributes } = useConversationRequiredAttributes();
const { localizedCountries, localizedLanguages } = useLocalizedLists();

// computed

const hasAppliedFilters = computed(() => {
  return appliedFilters.value.length !== 0;
});

const filterValueNames = values => {
  const normalizedValues = Array.isArray(values) ? values : [values];

  return normalizedValues
    .map(value => {
      if (value && typeof value === 'object') {
        return value.name || value.id;
      }

      return value;
    })
    .map(value => String(value || '').trim())
    .filter(Boolean);
};

const labelNamesFromConditions = conditions =>
  (Array.isArray(conditions) ? conditions : [])
    .filter(condition => {
      const attributeKey = condition.attributeKey || condition.attribute_key;
      const filterOperator =
        condition.filterOperator || condition.filter_operator;
      return attributeKey === 'labels' && filterOperator === 'equal_to';
    })
    .flatMap(condition => filterValueNames(condition.values));

const activeFolder = computed(() => {
  if (props.foldersId) {
    const activeView = folders.value.filter(
      view => view.id === Number(props.foldersId)
    );
    const [firstValue] = activeView;
    return firstValue;
  }
  return undefined;
});

const activeFolderName = computed(() => {
  return activeFolder.value?.name;
});

const previousStatusBeforeConcludedTab = ref(wootConstants.STATUS_TYPE.OPEN);
const previousAssigneeTabBeforeHistory = ref(aceleresDefaultAssigneeTab);
const aceleresTabToQuery = {
  novos: 'novos',
  [wootConstants.ASSIGNEE_TYPE.ME]: 'meus',
  outros: 'outros',
  [wootConstants.ASSIGNEE_TYPE.PERSONAL]: 'pessoais',
  [CONCLUDED_ASSIGNEE_TAB]: 'concluidos',
  [ARCHIVED_ASSIGNEE_TAB]: 'arquivadas',
};
const aceleresQueryToTab = {
  todas: 'novos',
  novos: 'novos',
  meus: wootConstants.ASSIGNEE_TYPE.ME,
  outros: 'outros',
  pessoais: wootConstants.ASSIGNEE_TYPE.PERSONAL,
  concluidos: CONCLUDED_ASSIGNEE_TAB,
  arquivadas: ARCHIVED_ASSIGNEE_TAB,
};

function normalizeAceleresBoxQuery(value) {
  const rawValue = Array.isArray(value) ? value[0] : value;
  return typeof rawValue === 'string' ? rawValue.trim().toLowerCase() : '';
}

function tabFromAceleresBoxQuery(value) {
  const selectedTab =
    aceleresQueryToTab[normalizeAceleresBoxQuery(value)] || null;

  if (
    selectedTab === wootConstants.ASSIGNEE_TYPE.PERSONAL &&
    !shouldShowPersonalTab.value
  ) {
    return null;
  }

  if (
    shouldMergeArchivedIntoConcluded.value &&
    selectedTab === ARCHIVED_ASSIGNEE_TAB
  ) {
    return CONCLUDED_ASSIGNEE_TAB;
  }

  return selectedTab;
}

function queryFromAceleresTab(tab) {
  return aceleresTabToQuery[tab] || null;
}

function aceleresBoxForTab(tab) {
  if (
    shouldMergeArchivedIntoConcluded.value &&
    tab === CONCLUDED_ASSIGNEE_TAB
  ) {
    return undefined;
  }

  return ACELERES_TAB_BOXES[tab] || undefined;
}

function setAceleresBoxQuery(tab) {
  const nextBox = queryFromAceleresTab(tab);
  if (!nextBox) {
    return;
  }

  const currentBox = normalizeAceleresBoxQuery(
    route.query[ACELERES_BOX_QUERY_PARAM]
  );
  if (currentBox === nextBox) {
    return;
  }

  router.replace({
    name: route.name,
    params: route.params,
    query: {
      ...route.query,
      [ACELERES_BOX_QUERY_PARAM]: nextBox,
    },
  });
}

function syncAssigneeTabFromRoute({
  immediateStats = false,
  fetchData = true,
} = {}) {
  const rawBox = normalizeAceleresBoxQuery(
    route.query[ACELERES_BOX_QUERY_PARAM]
  );
  const selectedTab = tabFromAceleresBoxQuery(
    route.query[ACELERES_BOX_QUERY_PARAM]
  );

  if (rawBox === 'pessoais' && !shouldShowPersonalTab.value) {
    setAceleresBoxQuery('novos');
    return;
  }

  if (rawBox === 'todas') {
    setAceleresBoxQuery('novos');
  }

  if (rawBox === 'arquivadas' && shouldMergeArchivedIntoConcluded.value) {
    setAceleresBoxQuery(CONCLUDED_ASSIGNEE_TAB);
  }

  if (!selectedTab || activeAssigneeTab.value === selectedTab) {
    return;
  }

  updateAssigneeTab(selectedTab, {
    immediateStats,
    fetchData,
    updateRouteQuery: false,
  });
}

const hasActiveFolders = computed(() => {
  return Boolean(activeFolder.value && props.foldersId !== 0);
});

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return hasAppliedFilters.value || hasActiveFolders.value;
});

// Aceleres: versoes "ForUI" das flags — consideram filtros aplicados EXCETO o
// filtro Aceleres (que tem UI propria). Mantem abas/header usaveis enquanto o
// filtro Aceleres esta ativo; folders continuam escondendo as abas. (Sem isso,
// essas refs ficavam indefinidas no template.)
const hasAppliedFiltersForUI = computed(
  () => hasAppliedFilters.value && !aceleresFilterApplied.value
);
const hasAppliedFiltersOrActiveFoldersForUI = computed(
  () => hasAppliedFiltersForUI.value || hasActiveFolders.value
);

const activeConversationLabel = computed(() => {
  const selectedLabel = qfLabelSlug.value || props.label;
  return selectedLabel;
});

const activeLabelFilterNames = computed(() => {
  const names = [
    activeConversationLabel.value,
    ...labelNamesFromConditions(activeAceleresFilterConditions.value),
    ...labelNamesFromConditions(appliedFilters.value),
  ]
    .map(label => String(label || '').trim())
    .filter(Boolean);

  return [...new Set(names)];
});

const activeTabLabel = computed(() => {
  const tabLabels = {
    novos: 'Novos',
    me: 'Meus',
    outros: 'Outros',
    personal: 'Pessoais',
    resolved: 'Concluídos',
    arquivadas: 'Arquivadas',
  };

  return tabLabels[activeAssigneeTab.value] || 'Conversas';
});

const labelFilterEmptyMessage = computed(() => {
  if (!activeLabelFilterNames.value.length) return '';

  return t('CHAT_LIST.FILTERED_EMPTY.WITH_LABELS', {
    labels: activeLabelFilterNames.value.join(', '),
    tab: activeTabLabel.value,
  });
});

const currentUserDetails = computed(() => {
  const { id, name } = currentUser.value;
  return { id, name };
});

const userPermissions = computed(() => {
  return getUserPermissions(currentUser.value, currentAccountId.value);
});

const unreadMineCount = computed(() => {
  try {
    // Aceleres: a aba "Novas" (mineUnread badge) deve ter contagem GLOBAL,
    // independente da caixa ativa.
    const f = { ...conversationFilters.value };
    const list = mineChatsList.value(f) || [];
    return list.filter(c => (c.unread_count || 0) > 0).length;
  } catch (e) {
    return 0;
  }
});

const isVisibleConcludedConversationForCurrentUser = conversation => {
  if (conversation?.status !== wootConstants.STATUS_TYPE.RESOLVED) {
    return false;
  }

  // A caixa de histórico é autorizada pelo servidor. Uma conversa concluída
  // pode estar sem responsável atual depois da conclusão, mas continuar
  // visível para quem teve acesso à instância naquele momento.
  if (isSellerVisibilityProfile.value) return true;

  if (!isSupervisorUser.value) {
    return true;
  }

  return true;
};

const isArchivedConversation = conversation => {
  const value = conversation?.additional_attributes?.aceleres_archived;
  return value === true || value === 'true';
};

const isVisibleConversationForHistoryTab = conversation => {
  if (!isVisibleConcludedConversationForCurrentUser(conversation)) {
    return false;
  }

  if (
    shouldMergeArchivedIntoConcluded.value &&
    activeAssigneeTab.value === CONCLUDED_ASSIGNEE_TAB
  ) {
    return true;
  }

  return activeAssigneeTab.value === ARCHIVED_ASSIGNEE_TAB
    ? isArchivedConversation(conversation)
    : !isArchivedConversation(conversation);
};

const conversationCountForTab = tab => {
  const box = ACELERES_TAB_BOXES[tab];
  const unreadCount = conversationStats.value?.aceleresBoxUnreadCounts?.[box];
  return typeof unreadCount === 'number' ? unreadCount : 0;
};

// Os totais por caixa (`aceleresBoxCounts`) existiam aqui apenas para limitar
// a paginação da lista; quem fecha a lista agora é o servidor (página vazia).
// Os badges das abas continuam usando `conversationCountForTab` (não lidas).

// Em toda caixa menos Outros o badge conta o que ESTE usuario ainda nao leu,
// e some assim que ele le. Outros e a excecao declarada (ver o countHint dela).
const MINHAS_NAO_LIDAS_HINT = 'Conversas que voce ainda nao leu';

const assigneeTabItems = computed(() => {
  const items = [
    {
      key: 'novos',
      name: 'Novos',
      count: conversationCountForTab('novos'),
      countHint: MINHAS_NAO_LIDAS_HINT,
    },
    {
      key: wootConstants.ASSIGNEE_TYPE.ME,
      name: 'Meus',
      count: conversationCountForTab(wootConstants.ASSIGNEE_TYPE.ME),
      countHint: MINHAS_NAO_LIDAS_HINT,
    },
  ];
  if (canSeeOthersBox.value) {
    items.push({
      key: 'outros',
      name: 'Outros',
      count: conversationCountForTab('outros'),
      // Outros é caixa de supervisão: por decisão de produto o número é o não
      // lido do VENDEDOR responsável, e por isso não cai quando quem
      // supervisiona abre a conversa (ver
      // ConversationFinder#box_unread_perspective). Sem dizer isso na tela, o
      // comportamento correto parece defeito.
      countHint: 'Conversas que o vendedor responsável ainda não leu',
    });
  }
  if (shouldShowPersonalTab.value) {
    items.push({
      key: wootConstants.ASSIGNEE_TYPE.PERSONAL,
      name: 'Pessoais',
      count: conversationCountForTab(wootConstants.ASSIGNEE_TYPE.PERSONAL),
      countHint: MINHAS_NAO_LIDAS_HINT,
    });
  }
  return items;
});

const quickSearchConversationHeading = computed(() => 'Conversas encontradas');

// Aceleres 31/08/2026: um cliente pode ter varios atendimentos abertos e a
// busca passou a listar TODOS. Duas linhas do mesmo contato tem o mesmo nome e
// o mesmo telefone, entao esta e a linha que diz de qual atendimento se trata:
// numero da conversa, instancia por onde ele corre, situacao e vendedor. O
// numero vem primeiro porque e o unico dado que nunca se repete -- duas
// conversas podem coincidir em instancia, situacao e vendedor ao mesmo tempo.
function quickSearchConversationMeta(cv) {
  const attendance = cv?.aceleres_attendance || {};
  const parts = [`#${cv?.id}`];

  const inbox = inboxDisplayName(attendance.inbox_name || cv?.inbox?.name);
  if (inbox) parts.push(inbox);

  if (attendance.status_label) parts.push(attendance.status_label);
  if (attendance.assignee_name) parts.push(attendance.assignee_name);

  return parts.join(' · ');
}

const supportsUnreadSubfilter = tab => {
  // A subaba Todos/Não lidas também fica disponível em Novos.
  if (tab === 'novos') return true;
  return [
    wootConstants.ASSIGNEE_TYPE.ME,
    'outros',
    wootConstants.ASSIGNEE_TYPE.PERSONAL,
  ].includes(tab);
};

const unreadSubfilterForTab = tab => {
  if (!supportsUnreadSubfilter(tab)) {
    return UNREAD_SUBFILTER_ALL;
  }

  return unreadSubfiltersByTab.value[tab] || UNREAD_SUBFILTER_ALL;
};

const shouldShowUnreadSubfilters = computed(() => {
  return supportsUnreadSubfilter(activeAssigneeTab.value);
});

const activeUnreadSubfilter = computed(() =>
  unreadSubfilterForTab(activeAssigneeTab.value)
);

// Aceleres: o vendedor responsável (ou "Sem vendedor responsável" na fila
// de novos) deve aparecer em todo card de atendimento, não só nas abas
// filtradas/Outros/histórico — sem isso o nome da instância era a única
// informação visível no card, e usuários o confundiam com o vendedor.
// Exceção: na aba Pessoais a conversa já é do próprio vendedor, então o
// nome não agrega informação.
const showAssigneeInConversationCard = computed(() => {
  return activeAssigneeTab.value !== wootConstants.ASSIGNEE_TYPE.PERSONAL;
});

const currentPageFilterKey = computed(() => {
  if (isHistoryTab(activeAssigneeTab.value)) {
    return activeAssigneeTab.value;
  }
  return hasAppliedFiltersOrActiveFolders.value
    ? 'appliedFilters'
    : activeAssigneeTab.value;
});

function conversationStatusForTab(tab) {
  if (isHistoryTab(tab)) {
    return wootConstants.STATUS_TYPE.RESOLVED;
  }

  if (isHistoryTab(activeAssigneeTab.value)) {
    return (
      previousStatusBeforeConcludedTab.value || wootConstants.STATUS_TYPE.OPEN
    );
  }

  return activeStatus.value;
}

function conversationViewCacheKeyForTab(tab) {
  return [
    currentAccountId.value || '',
    currentUser.value?.id || '',
    tab,
    // "Todos" e "Não lidos" são visões diferentes. Manter o subfiltro na
    // chave evita restaurar um snapshot completo quando a tela solicitou
    // somente as conversas não lidas (ou o contrário).
    unreadSubfilterForTab(tab),
    shouldMergeArchivedIntoConcluded.value && tab === CONCLUDED_ASSIGNEE_TAB
      ? 'mobile-history'
      : '',
    conversationStatusForTab(tab),
    activeSortBy.value,
    props.conversationInbox || activeInboxFilter.value || '',
    props.teamId || qfTeamId.value || '',
    props.conversationType || '',
    activeConversationLabel.value || '',
    iaOnly.value ? 'ia' : '',
    qfPriority.value || '',
    qfAgentId.value || '',
  ].join(':');
}

const conversationViewCacheKey = computed(() =>
  conversationViewCacheKeyForTab(currentPageFilterKey.value)
);

const inbox = useFunctionGetter('inboxes/getInbox', activeInbox);
const currentFiltersPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  currentPageFilterKey
);
const hasCurrentPageEndReached = useFunctionGetter(
  'conversationPage/getHasEndReached',
  currentPageFilterKey
);

const conversationCustomAttributes = useFunctionGetter(
  'attributes/getAttributesByModel',
  'conversation_attribute'
);

const conversationListPagination = computed(() => {
  // A página seguinte depende do estado de paginação da visão atual, não do
  // tamanho da lista anterior. O tamanho podia manter uma visão antiga após
  // trocar filtro/ordenação e fazer a primeira busca nova começar na página 2.
  return (currentFiltersPage.value ?? 0) + 1;
});

const conversationFilters = computed(() => {
  const tab = activeAssigneeTab.value;
  // Pessoais é a caixa do dono da conexão, não uma sublista do canal que
  // estava aberto antes. Manter `inboxId` aqui fazia a mensagem chegar pelo
  // WebSocket e atualizar o contador, mas a listagem continuava presa ao outro
  // canal; a conversa só surgia depois de selecionar manualmente sua inbox.
  // As demais caixas preservam somente o filtro de canal explicitamente
  // escolhido na rota ou no filtro da lista; a inbox global selecionada para
  // navegação não pode restringir a visão padrão.
  const selectedInboxId = resolveConversationListInboxId({
    routeInboxId:
      tab === wootConstants.ASSIGNEE_TYPE.PERSONAL
        ? null
        : props.conversationInbox,
    filterInboxId:
      tab === wootConstants.ASSIGNEE_TYPE.PERSONAL
        ? null
        : activeInboxFilter.value,
  });
  // Mapeia a key da aba p/ o nome da caixa no backend. Cuidado: a aba Meus
  // tem key 'me' (ASSIGNEE_TYPE.ME), não 'meus' — sem isso aceleresBox ficava
  // undefined e Meus não filtrava (mostrava tudo igual a Novos).
  const boxName = aceleresBoxForTab(tab);
  return {
    inboxId: selectedInboxId || undefined,
    // Aceleres 4 caixas: o backend filtra por aceleres_box e IGNORA assignee_type
    // quando aceleres_box esta presente. Mantemos assigneeType = key da aba ativa
    // so pro page-tracking (buildConversationList keya a pagina por aqui = currentPageFilterKey).
    // Sem isso a pagina nao avanca e o scroll nao carrega mais (trava na pag 1).
    assigneeType: tab,
    status: isHistoryTab(tab)
      ? wootConstants.STATUS_TYPE.RESOLVED
      : activeStatus.value,
    sortBy: activeSortBy.value,
    page: conversationListPagination.value,
    labels: activeConversationLabel.value
      ? [activeConversationLabel.value]
      : undefined,
    teamId: props.teamId || qfTeamId.value || undefined,
    conversationType: props.conversationType || undefined,
    atendidoIa: iaOnly.value || undefined,
    aceleresBox: boxName,
    priority: qfPriority.value || undefined,
    assigneeId: qfAgentId.value || undefined,
    unreadOnly:
      activeUnreadSubfilter.value === UNREAD_SUBFILTER_ONLY || undefined,
    // A lista de não lidas precisa acompanhar o mesmo estado fresco usado
    // pelo contador; o cache de lista separado podia manter uma conversa a
    // menos enquanto o badge já refletia a alteração.
    skipCache:
      activeUnreadSubfilter.value === UNREAD_SUBFILTER_ONLY ||
      aceleresForceServerListRefresh.value ||
      undefined,
    skipCounts: true,
  };
});

const conversationStatsFilters = computed(() => {
  const filters = { ...conversationFilters.value };
  const labelNames = activeLabelFilterNames.value;

  // The stats endpoint already receives aceleres_box. Omitting assigneeType
  // prevents the legacy store guard from discarding labels for Pessoais.
  delete filters.assigneeType;
  delete filters.skipCache;

  if (labelNames.length) {
    filters.labels = labelNames;
  } else {
    delete filters.labels;
  }

  return filters;
});

const activeTeam = computed(() => {
  if (props.teamId) {
    return getTeamFn.value(props.teamId);
  }
  return {};
});

const pageTitle = computed(() => {
  return 'Painel de atendimento';
});

const isDashboardHomeRoute = computed(() => route.name === 'home');

// Espelha `ConversationFinder#group_conversation_sql`: o marcador
// `is_whatsapp_group` vem da ponte WAME atual, e o identificador
// `group:<jid>@g.us` existe desde sempre — nenhuma das duas fontes cobre todo
// o histórico sozinha.
function isGroupListConversation(conversation) {
  if (isGroupConversation(conversation)) return true;

  const identifier = String(conversation?.meta?.sender?.identifier || '');
  return identifier.endsWith('@g.us');
}

function filterByAssigneeTab(conversations) {
  const candidates = filterServerValidatedConversations(conversations).filter(
    conversation =>
      !isOperationalAceleresTab(activeAssigneeTab.value) ||
      conversation?.status !== wootConstants.STATUS_TYPE.RESOLVED
  );
  const currentUserId = Number(currentUser.value?.id);
  const assigneeId = conversation =>
    Number(conversation?.meta?.assignee?.id || 0);
  const isAssignedToCurrentUser = conversation =>
    !!currentUserId && assigneeId(conversation) === currentUserId;
  const isUnassigned = conversation => !assigneeId(conversation);
  const isStarted = conversation => isAceleresConversationStarted(conversation);
  const hasPersonalMarker = conversation =>
    hasPersonalConversationMarker(conversation);
  const isClaimedPersonalByCurrentUser = conversation => {
    const attributes = conversation?.additional_attributes || {};
    const ownerId = Number(
      conversation?.inbox_owner_chatwoot_user_id ||
        attributes.personal_owner_chatwoot_user_id ||
        0
    );

    return Boolean(attributes.personal_claimed_at) && ownerId === currentUserId;
  };

  if (activeAssigneeTab.value === 'novos') {
    return candidates.filter(
      c =>
        (isAssignedToCurrentUser(c) &&
          !isStarted(c) &&
          !hasPersonalMarker(c)) ||
        ((isManagerRole.value || isSupervisorUser.value) &&
          isUnassigned(c) &&
          // A fila sem responsável inclui atendimentos já iniciados, como no
          // backend. Sem isso, a página chega mas some inteira da interface.
          !hasPersonalMarker(c)) ||
        // Grupo do WhatsApp: quem tem acesso ao canal enxerga, com ou sem
        // responsável — é a mesma regra do `grupos_scope` no backend. Sem esta
        // linha o servidor mandava os grupos na página de Novos e o filtro
        // local os descartava por terem assignee de outro vendedor: em
        // homologação, 35 das 51 conversas da caixa sumiam da tela.
        (isGroupListConversation(c) && !hasPersonalMarker(c))
    );
  }

  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ME) {
    // Pessoais e Meus sao disjuntas por construcao, e quem responde se a
    // conversa ocupa Pessoais e o backend (`aceleres_personal_box`, que é
    // `Aceleres::PersonalBox#occupied?`). Sem essa linha o espelho local
    // divergia do servidor justamente na conversa pessoal legada: `isStarted`
    // cai em `first_reply_created_at` quando o atributo explicito nao existe,
    // e o Chatwoot carimba esse campo em qualquer resposta que sai — inclusive
    // a que o dono manda do proprio celular. A conversa vinha em Pessoais e
    // aparecia tambem aqui.
    return candidates.filter(
      c =>
        isAssignedToCurrentUser(c) &&
        !isPersonalConversationForCurrentUser(c) &&
        (isStarted(c) || isClaimedPersonalByCurrentUser(c))
    );
  }

  if (activeAssigneeTab.value === 'outros') {
    // "Outros" é exclusiva de supervisores (supervised_scope no backend).
    // A lista já vem filtrada pelo supervised_scope do backend. Não repetir
    // aqui a regra como "somente vendedores": o escopo também pode incluir
    // conversas de uma inbox/equipe supervisionada atribuídas a um operador
    // que não tem role de vendedor. Repetir esse filtro fazia o badge contar
    // a conversa, mas a lista descartá-la (ex.: conversa #729 / Pietro).

    return candidates.filter(
      c =>
        isSupervisorUser.value &&
        !!assigneeId(c) &&
        !isAssignedToCurrentUser(c) &&
        !hasPersonalMarker(c)
    );
  }

  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.UNASSIGNED) {
    return candidates.filter(c => isUnassigned(c));
  }

  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.PERSONAL) {
    // O backend filtra a caixa, mas o store pode conter itens antigos de outra
    // aba durante a troca. O marcador local impede que conversas de Novos/Meus
    // vazem para Pessoais; inboxes próprias também contam mesmo sem a etiqueta.
    return candidates.filter(c => isPersonalConversationForCurrentUser(c));
  }

  return candidates;
}

const hasVisibleDraft = useMapGetter('draftMessages/hasVisibleDraft');

function normalizePinnedConversation(conversation) {
  if (!conversation) return conversation;

  return {
    ...conversation,
    is_pinned:
      conversation.is_pinned ?? conversation.meta?.is_pinned ?? false,
    pinned_at:
      conversation.pinned_at ?? conversation.meta?.pinned_at ?? null,
  };
}

// Aceleres: conversas fixadas ficam sempre acima das demais. Dentro de cada
// grupo, a ordem recebida é preservada; rascunhos ficam acima das não fixadas.
const hoistConversationsWithDraft = conversations => {
  const pinned = [];
  const withDraft = [];
  const rest = [];

  conversations.map(normalizePinnedConversation).forEach(conversation => {
    if (conversation?.is_pinned) {
      pinned.push(conversation);
      return;
    }
    if (hasVisibleDraft.value(conversation?.id)) {
      withDraft.push(conversation);
      return;
    }
    rest.push(conversation);
  });

  return pinned.length || withDraft.length
    ? [...pinned, ...withDraft, ...rest]
    : conversations;
};

const filterByActiveAceleresTab = conversations => {
  const allCandidates = Array.isArray(conversations) ? conversations : [];
  // Conversas Pessoais são classificadas pela origem da instância e não
  // participam dos filtros operacionais de funil. Aplicar o funil antes desta
  // aba repetiria o defeito em que a conversa só surgia após trocar um filtro.
  const candidates =
    activeAssigneeTab.value === 'personal'
      ? allCandidates
      : allCandidates.filter(isConversationInSelectedFunnel);

  if (isHistoryTab(activeAssigneeTab.value)) {
    return candidates.filter(isVisibleConversationForHistoryTab);
  }

  return filterByAssigneeTab(candidates);
};

const conversationList = computed(() => {
  if (aceleresViewTransitioning.value) {
    return [];
  }

  let localConversationList = [];

  if (!hasAppliedFiltersOrActiveFolders.value) {
    const filters = conversationFilters.value;
    if (
      props.conversationType === wootConstants.CONVERSATION_TYPE.PARTICIPATING
    ) {
      localConversationList = filterByAssigneeTab(
        participatingChatsList.value(filters)
      );
    } else if (isHistoryTab(activeAssigneeTab.value)) {
      localConversationList = filterByActiveAceleresTab(
        allConversationsList.value
      );
    } else {
      // Aceleres 4 caixas: backend ja filtrou por aceleres_box
      // (novos/meus/outros/pessoais). Mantemos um guard local para evitar
      // vazamento visual quando o store ainda tem itens de outra aba.
      localConversationList = filterByActiveAceleresTab(
        allChatList.value(filters)
      );
    }
  } else {
    // O resultado do filtro avançado vem do servidor já limitado pela caixa,
    // mas pode conter itens de uma resposta ou cache anterior durante a troca
    // de aba. Reaplicar o escopo local mantém a etiqueta como refinamento da
    // aba atual, sem permitir que uma conversa pessoal apareça em Novos.
    localConversationList = filterByActiveAceleresTab(chatLists.value);
  }

  if (activeFolder.value) {
    const { payload } = activeFolder.value.query;
    localConversationList = localConversationList.filter(conversation => {
      return matchesFilters(conversation, payload);
    });
  }

  // O endpoint já aplica `unread_only=true`, e os itens retornados preservam
  // o unread_count mesmo quando pertencem a outro vendedor. O store, porém,
  // mantém a conversa atualmente aberta mesmo fora da página requisitada;
  // removemos esse item extra para a lista visual continuar com exatamente as
  // conversas não lidas retornadas pelo servidor.
  //
  // Exceção: a conversa ABERTA nunca desaparece. Sem ela, iniciar um
  // atendimento e mandar a primeira mensagem sumia com a conversa de TODAS as
  // abas — não existe recebida não lida ainda, então nenhum subfiltro "Não
  // lidos" a mostra, e o vendedor perde de vista justamente o que acabou de
  // fazer, com o chat aberto na frente dele (relato de 18/08/2026).
  if (activeUnreadSubfilter.value === UNREAD_SUBFILTER_ONLY) {
    const openConversationId = activeQuickSearchConversationId.value;
    localConversationList = localConversationList.filter(
      conversation =>
        Number(conversation?.unread_count || 0) > 0 ||
        (openConversationId > 0 &&
          Number(conversation?.id) === openConversationId)
    );
  }

  return hoistConversationsWithDraft(localConversationList);
});

const hasCompleteCachedAceleresView = computed(() => {
  const cacheVersion = aceleresConversationCacheVersion.value;
  return (
    cacheVersion >= 0 &&
    aceleresStartupTabsReady.value &&
    !hasAppliedFiltersOrActiveFolders.value &&
    Object.hasOwn(ACELERES_TAB_BOXES, activeAssigneeTab.value) &&
    aceleresCompleteConversationCacheKeys.has(conversationViewCacheKey.value)
  );
});

const shouldShowChatListLoadingIndicator = computed(() => {
  return (
    aceleresViewTransitioning.value ||
    aceleresAppendInFlight.value ||
    (chatListLoading.value &&
      !hasCompleteCachedAceleresView.value &&
      !aceleresConversationCache.has(conversationViewCacheKey.value))
  );
});

const showEndOfListMessage = computed(() => {
  return (
    conversationList.value.length &&
    hasCurrentPageEndReached.value &&
    !shouldShowChatListLoadingIndicator.value
  );
});

// Aceleres 2026-09-01: o fim da lista passou a ser UMA linha, com a
// contagem do que esta carregado. Antes eram duas, vindas do i18n
// (`CHAT_LIST.EOF`, com um emoji, mais `CHAT_LIST.OPEN_CONVERSATIONS` por
// baixo). O numero e o mesmo `conversationList.length` que a segunda linha
// ja usava, portanto nao ha contagem nova a manter em dia.
const endOfListMessage = computed(
  () => `Todas as conversas carregadas · ${conversationList.value.length}`
);

const allConversationsSelected = computed(() => {
  return (
    conversationList.value.length === selectedConversations.value.length &&
    conversationList.value.every(el =>
      selectedConversations.value.includes(el.id)
    )
  );
});

const uniqueInboxes = computed(() => {
  return [...new Set(selectedInboxes.value)];
});

// ---------------------- Methods -----------------------
function setFiltersFromUISettings() {
  const { conversations_filter_by: filterBy = {} } = uiSettings.value;
  const { status } = filterBy;
  activeStatus.value = status || wootConstants.STATUS_TYPE.OPEN;

  // Aceleres: a ordenacao e estado de sessao, nao preferencia salva.
  // O padrao e sempre "Ultimas interacoes"; a escolha manual vive no store
  // (memoria), entao sobrevive ao remount do ChatList quando o usuario sai
  // para outra tela e volta, mas volta ao padrao no refresh e no logout,
  // que recarregam a pagina e zeram o Vuex. Ler order_by do uiSettings
  // fazia o oposto: o valor gravado no servidor sobrevivia ao refresh e ao
  // logout, e o remount o reimpunha por cima da escolha da sessao.
  const sessionSortBy = store.getters.getChatSortFilter;
  activeSortBy.value = Object.values(wootConstants.SORT_BY_TYPE).includes(
    sessionSortBy
  )
    ? sessionSortBy
    : wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC;
}

function emitConversationLoaded(conversations) {
  // A resposta descartada por uma troca de aba não pode publicar o estado
  // antigo na lista nem sobrescrever o cache da aba atualmente selecionada.
  if (conversations === undefined) return;

  chatsOnView.value = conversationList.value;
  cacheCurrentAceleresConversationList();
  emit('conversationLoad');
}

function isCacheableAceleresTab() {
  return (
    !hasAppliedFiltersOrActiveFolders.value &&
    Object.hasOwn(ACELERES_TAB_BOXES, activeAssigneeTab.value)
  );
}

// `loadedPage` e a ultima pagina que a visao ja pediu ao servidor. Omitir o
// argumento preserva a pagina que ja estava registrada (o caso de quem so
// re-hidrata a mesma lista); quem substitui a lista por uma primeira pagina
// precisa passar 1 explicitamente.
function setCachedAceleresConversationList(
  cacheKey,
  conversations,
  loadedPage
) {
  const normalizedConversations = conversations.map(
    normalizePinnedConversation
  );
  const previousConversations =
    aceleresConversationCache.get(cacheKey) || [];
  const previousById = new Map(
    previousConversations.map(conversation => [Number(conversation?.id), conversation])
  );
  const synchronizedConversations = normalizedConversations.map(conversation => {
    const previous = previousById.get(Number(conversation?.id));
    if (previous?.is_pinned && conversation?.is_pinned === false) {
      return {
        ...conversation,
        is_pinned: true,
        pinned_at: previous.pinned_at,
      };
    }
    return conversation;
  });
  aceleresConversationCache.delete(cacheKey);
  aceleresConversationCache.set(cacheKey, [...synchronizedConversations]);
  const page = Math.max(
    1,
    Number(loadedPage) || aceleresConversationCachePages.get(cacheKey) || 1
  );
  aceleresConversationCachePages.set(cacheKey, page);
  if (isHistoryAceleresCacheKey(cacheKey)) {
    aceleresHistoryCacheStamps.set(cacheKey, Date.now());
  }
  persistAceleresConversationList(cacheKey, synchronizedConversations, page);

  if (aceleresConversationCache.size > ACELERES_CONVERSATION_CACHE_LIMIT) {
    const oldestCacheKey = aceleresConversationCache.keys().next().value;
    aceleresConversationCache.delete(oldestCacheKey);
    aceleresCompleteConversationCacheKeys.delete(oldestCacheKey);
    aceleresConversationCachePages.delete(oldestCacheKey);
    aceleresHistoryCacheStamps.delete(oldestCacheKey);
    removePersistedAceleresConversationList(oldestCacheKey);
  }

  aceleresConversationCacheVersion.value += 1;
}

function persistedAceleresConversationListKey(cacheKey) {
  return `${ACELERES_PERSISTED_LIST_CACHE_PREFIX}:${cacheKey}`;
}

function removePersistedAceleresConversationList(cacheKey) {
  aceleresConversationCachePages.delete(cacheKey);
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.removeItem(
      persistedAceleresConversationListKey(cacheKey)
    );
  } catch {
    // Storage indisponivel nao impede o cache em memoria nem a busca de rede.
  }
}

function persistAceleresConversationList(cacheKey, conversations, loadedPage) {
  if (
    typeof window === 'undefined' ||
    !isOperationalAceleresCacheKey(cacheKey) ||
    !Array.isArray(conversations) ||
    !conversations.length
  ) {
    return;
  }

  const persistedConversations = conversations.slice(
    0,
    ACELERES_PERSISTED_LIST_MAX_ITEMS
  );
  // O snapshot persistido e cortado; a pagina precisa acompanhar o corte,
  // senao o scroll retomaria depois de conversas que ficaram de fora dele.
  const persistedPage = Math.max(
    1,
    Math.min(
      Number(loadedPage) || 1,
      Math.ceil(persistedConversations.length / ACELERES_CONVERSATIONS_PER_PAGE)
    )
  );

  try {
    window.sessionStorage.setItem(
      persistedAceleresConversationListKey(cacheKey),
      JSON.stringify({
        savedAt: Date.now(),
        page: persistedPage,
        conversations: persistedConversations,
      })
    );
  } catch {
    // A lista de rede continua sendo a fonte autoritativa quando o navegador
    // bloqueia storage ou a quota da aba foi atingida.
  }
}

function readPersistedAceleresConversationList(cacheKey) {
  if (typeof window === 'undefined') return null;

  const storageKey = persistedAceleresConversationListKey(cacheKey);
  try {
    const cached = JSON.parse(window.sessionStorage.getItem(storageKey));
    if (
      !Array.isArray(cached?.conversations) ||
      !cached.conversations.length ||
      !cached.savedAt ||
      Date.now() - cached.savedAt > ACELERES_PERSISTED_LIST_CACHE_TTL
    ) {
      window.sessionStorage.removeItem(storageKey);
      return null;
    }

    return {
      conversations: cached.conversations,
      page: Math.max(1, Number(cached.page) || 1),
    };
  } catch {
    removePersistedAceleresConversationList(cacheKey);
    return null;
  }
}

function isOperationalAceleresCacheKey(cacheKey) {
  const tab = String(cacheKey).split(':')[2];
  return isOperationalAceleresTab(tab);
}

function isHistoryAceleresCacheKey(cacheKey) {
  return isHistoryTab(String(cacheKey).split(':')[2]);
}

// Remove conversas concluídas de TODOS os snapshots persistidos de caixa
// operacional.
//
// `synchronizeConversationInAceleresCaches` percorre `aceleresConversationCache`
// (Map em memória), que nasce vazio a cada carregamento da página. Os snapshots
// que sobraram no sessionStorage de um carregamento anterior nunca eram
// visitados, então a conversa concluída continuava lá — e voltava para a caixa
// na primeira vez que o usuário abrisse aquela aba, porque
// `mergeConversationPage` substitui o registro do store pelo do snapshot.
const aceleresPurgedResolvedIds = new Set();
let aceleresPurgePendente = null;

function executarPurgePersistido(conversationIds) {
  let storageKeys = [];
  try {
    storageKeys = Object.keys(window.sessionStorage).filter(key =>
      key.startsWith(`${ACELERES_PERSISTED_LIST_CACHE_PREFIX}:`)
    );
  } catch {
    // Storage bloqueado: o cache em memoria e a rede seguem valendo.
    return;
  }

  storageKeys.forEach(storageKey => {
    const cacheKey = storageKey.slice(
      ACELERES_PERSISTED_LIST_CACHE_PREFIX.length + 1
    );
    // As chaves que estão no Map já foram tratadas pelo laço em memória, que
    // reescreve o snapshot junto. Mexer aqui de novo só desfaria aquilo.
    if (aceleresConversationCache.has(cacheKey)) return;
    if (!isOperationalAceleresCacheKey(cacheKey)) return;

    const snapshot = readPersistedAceleresConversationList(cacheKey);
    if (!snapshot) return;

    const restantes = snapshot.conversations.filter(
      cached => !conversationIds.has(Number(cached?.id))
    );
    if (restantes.length === snapshot.conversations.length) return;

    if (!restantes.length) {
      removePersistedAceleresConversationList(cacheKey);
      return;
    }
    persistAceleresConversationList(cacheKey, restantes, snapshot.page);
  });
}

function purgeConversationFromPersistedAceleresLists(conversationId) {
  if (typeof window === 'undefined' || !conversationId) return;
  // O watcher de status reexecuta a sincronizacao para TODA conversa resolvida
  // do store. Sem a marca, a varredura rodaria dezenas de vezes por evento na
  // aba Concluidos; sem o agrupamento, a PRIMEIRA passagem ainda faria uma
  // varredura por conversa (50 conversas x 4 caixas = 200 JSON.parse seguidos).
  if (aceleresPurgedResolvedIds.has(conversationId)) return;
  aceleresPurgedResolvedIds.add(conversationId);

  if (aceleresPurgePendente) {
    aceleresPurgePendente.add(conversationId);
    return;
  }

  aceleresPurgePendente = new Set([conversationId]);
  queueMicrotask(() => {
    const pendentes = aceleresPurgePendente;
    aceleresPurgePendente = null;
    executarPurgePersistido(pendentes);
  });
}

function realtimeMessageSortValue(message) {
  const numericCreatedAt = Number(message?.created_at);
  if (Number.isFinite(numericCreatedAt)) return numericCreatedAt;

  const parsedCreatedAt = Date.parse(message?.created_at || '');
  return Number.isFinite(parsedCreatedAt) ? parsedCreatedAt : 0;
}

function isNewerRealtimeMessage(message, currentMessage) {
  if (!currentMessage) return true;

  const timeDifference =
    realtimeMessageSortValue(message) -
    realtimeMessageSortValue(currentMessage);

  return (
    timeDifference > 0 ||
    (timeDifference === 0 &&
      Number(message?.id || 0) >= Number(currentMessage?.id || 0))
  );
}

function mergeRealtimeMessageIntoConversation(conversation, message) {
  if (!message?.id) return conversation;

  const messages = Array.isArray(conversation.messages)
    ? [...conversation.messages]
    : [];
  const existingMessageIndex = messages.findIndex(
    item => item.id === message.id
  );

  if (existingMessageIndex >= 0) {
    messages.splice(existingMessageIndex, 1, message);
  } else {
    messages.push(message);
  }

  const updates = { messages };
  const isActivity = Number(message.message_type) === MESSAGE_TYPE.ACTIVITY;

  if (!isActivity && !message.private) {
    const currentPreview = conversation.last_non_activity_message;
    if (isNewerRealtimeMessage(message, currentPreview)) {
      updates.last_non_activity_message = message;
    }
  }

  return { ...conversation, ...updates };
}

function synchronizeConversationInAceleresCaches(
  conversation,
  { preservePinned = true } = {}
) {
  const conversationId = Number(conversation?.id);
  if (!conversationId) return;
  const isResolved =
    conversation?.status === wootConstants.STATUS_TYPE.RESOLVED;
  // Reabrir a conversa devolve o direito a uma nova varredura: sem isto, uma
  // conversa concluída, reaberta e concluída de novo nunca seria limpa outra
  // vez dos snapshots persistidos.
  if (!isResolved) aceleresPurgedResolvedIds.delete(conversationId);

  aceleresConversationCache.forEach((cachedConversations, cacheKey) => {
    let conversationFound = false;
    const synchronizedConversations = isResolved
      ? cachedConversations.filter(cachedConversation => {
          const matches = Number(cachedConversation?.id) === conversationId;
          conversationFound ||= matches;
          return !matches || !isOperationalAceleresCacheKey(cacheKey);
        })
      : cachedConversations.map(cachedConversation => {
          if (Number(cachedConversation?.id) !== conversationId) {
            return cachedConversation;
          }

          conversationFound = true;
          const preservedPin =
            preservePinned &&
            cachedConversation.is_pinned &&
            conversation.is_pinned === false
              ? {
                  is_pinned: true,
                  pinned_at: cachedConversation.pinned_at,
                }
              : {};
          const synchronizedConversation = {
            ...cachedConversation,
            ...conversation,
            ...preservedPin,
            meta: {
              ...(cachedConversation.meta || {}),
              ...(conversation.meta || {}),
            },
            additional_attributes: {
              ...(cachedConversation.additional_attributes || {}),
              ...(conversation.additional_attributes || {}),
            },
          };

          return mergeRealtimeMessageIntoConversation(
            synchronizedConversation,
            conversation.message
          );
        });

    if (conversationFound) {
      if (isResolved && !synchronizedConversations.length) {
        aceleresConversationCache.delete(cacheKey);
        aceleresCompleteConversationCacheKeys.delete(cacheKey);
        removePersistedAceleresConversationList(cacheKey);
      } else {
        aceleresConversationCache.set(cacheKey, synchronizedConversations);
        persistAceleresConversationList(cacheKey, synchronizedConversations);
      }
    }
  });

  // Uma conversa concluída não pode voltar para a cache de Meus por meio da
  // regra de sincronização de atribuição. Ela será carregada exclusivamente
  // quando a aba Concluídos for aberta.
  if (isResolved) {
    purgeConversationFromPersistedAceleresLists(conversationId);
    return;
  }

  if (hasAppliedFiltersOrActiveFolders.value) return;

  const mineTab = wootConstants.ASSIGNEE_TYPE.ME;
  const mineCacheKey = conversationViewCacheKeyForTab(mineTab);
  const mineConversations = aceleresConversationCache.get(mineCacheKey) || [];
  const mineUnreadOnly =
    unreadSubfilterForTab(mineTab) === UNREAD_SUBFILTER_ONLY;
  // A conversa aberta é a exceção do subfiltro "Não lidos" (ver o filtro da
  // lista visual): ela não pode ser despejada da cache de Meus só por não ter
  // recebida não lida, senão enviar a primeira mensagem a faz desaparecer.
  const belongsToCachedMine =
    !mineUnreadOnly ||
    Number(conversation.unread_count || 0) > 0 ||
    conversationId === activeQuickSearchConversationId.value;

  if (!belongsToCachedMine) {
    aceleresConversationCache.set(
      mineCacheKey,
      mineConversations.filter(cached => Number(cached?.id) !== conversationId)
    );
    return;
  }

  if (
    !mineConversations.some(cached => Number(cached?.id) === conversationId)
  ) {
    setCachedAceleresConversationList(mineCacheKey, [
      conversation,
      ...mineConversations,
    ]);
  }
}

function cacheCurrentAceleresConversationList() {
  if (!isCacheableAceleresTab()) return;

  const cacheKey = conversationViewCacheKey.value;
  const conversations = conversationList.value || [];
  // O store já contém todas as páginas carregadas da visão. Reanexar o cache
  // antigo aqui fazia voltar conversas que o servidor já removeu do filtro.
  setCachedAceleresConversationList(
    cacheKey,
    conversations,
    currentFiltersPage.value || 1
  );
}

function startAceleresStartupPrefetch() {
  if (!Reflect.get(window, '__zoraxyLoadingActive')) return false;

  aceleresStartupPrefetchRequests += 1;
  if (aceleresStartupPrefetchRequests === 1) {
    window.startZoraxyConversationPrefetch?.({ targetCount: 1 });
  }

  return true;
}

function finishAceleresStartupPrefetch(wasTracked) {
  if (!wasTracked) return;

  aceleresStartupPrefetchRequests = 0;
  window.finishZoraxyConversationPrefetch?.();
}

// Aceleres: espera o navegador ficar ocioso antes de aquecer as outras abas.
// No boot a lista critica, os stats, o menu e 3 abas disputavam CPU/rede ao
// mesmo tempo — era essa soma que estourava o Rack::Timeout do servidor.
function waitForIdleBeforePrefetch(maxWaitMs = 2000) {
  return new Promise(resolve => {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(() => resolve(), { timeout: maxWaitMs });
      return;
    }
    setTimeout(resolve, 350);
  });
}

async function prefetchAceleresStartupTabs() {
  if (aceleresStartupTabsPromise) return aceleresStartupTabsPromise;

  // Aceleres 2026-09-10: Concluidos entra no aquecimento de boot.
  //
  // `assigneeTabItems` so lista as caixas operacionais -- Concluidos e
  // alcancado por outro controlo --, entao ele nunca era aquecido e a PRIMEIRA
  // abertura pagava o request inteiro com o spinner na tela. A query nao e o
  // motivo: medida em homolog na conta com 268 concluidas, a consulta que a
  // aba realmente manda (status=resolved, sem aceleres_box) custa 47ms morna,
  // contra 200ms de `novos`. O que pesa e o overhead por requisicao, igual em
  // todas as caixas (mediana de 600-1400ms fora de SQL e de views no journal
  // do Puma).
  //
  // Vai por ultimo de proposito: o `reduce` abaixo e sequencial com 350ms
  // entre abas, entao aquecer o historico nunca disputa com a caixa que o
  // usuario esta a ver.
  const tabs = assigneeTabItems.value
    .map(item => item.key)
    .filter(tab => isOperationalAceleresTab(tab));
  tabs.push(CONCLUDED_ASSIGNEE_TAB);
  if (!tabs.length) {
    aceleresStartupTabsReady.value = true;
    return Promise.resolve();
  }

  const selectedTab = tabs.includes(activeAssigneeTab.value)
    ? activeAssigneeTab.value
    : tabs[0];
  // The active list and metadata already compete during boot. Warming every
  // other tab here multiplied identical inbox/conversation work and delayed
  // the counters; inactive tabs can load on first selection instead.
  const orderedTabs = [selectedTab];

  const warmStartupTab = async tab => {
    const cacheKey = conversationViewCacheKeyForTab(tab);
    // A aba atual já foi buscada pela requisição crítica. Reutilizá-la
    // evita repetir imediatamente a mesma consulta ao entrar no inbox.
    const conversations =
      tab === selectedTab
        ? [...(conversationList.value || [])]
        : await store.dispatch('prefetchConversationList', {
            ...conversationFilters.value,
            assigneeType: tab,
            // `aceleresBoxForTab` em vez do mapa cru: em Concluidos com
            // `shouldMergeArchivedIntoConcluded` a caixa e omitida de
            // proposito (concluidas e arquivadas na mesma aba), e o mapa cru
            // aqueceria uma consulta diferente da que a aba faz.
            aceleresBox: aceleresBoxForTab(tab),
            // Sem isto o historico seria aquecido com `status` da caixa ativa
            // (aberta) e o snapshot viria vazio.
            status: conversationStatusForTab(tab),
            // Não herdar o subfiltro "Não lidas" ao aquecer outra aba.
            unreadOnly: undefined,
            // Contadores são carregados separadamente e em segundo plano.
            skipCounts: true,
            page: 1,
          });

    const tabConversations = Array.isArray(conversations)
      ? conversations
      : [];
    // O aquecimento das outras abas traz somente a primeira página; a aba
    // selecionada reaproveita a lista já carregada, que pode ter mais páginas.
    setCachedAceleresConversationList(
      cacheKey,
      tabConversations,
      tab === selectedTab ? currentFiltersPage.value || 1 : 1
    );

  };

  aceleresStartupTabsPromise = orderedTabs
    .reduce(
      (promise, tab, index) =>
        promise.then(async () => {
          await warmStartupTab(tab);
          if (index < orderedTabs.length - 1) {
            await new Promise(resolve => {
              setTimeout(resolve, 350);
            });
          }
        }),
      waitForIdleBeforePrefetch()
    )
    .then(() => {
      aceleresStartupTabsReady.value = true;
    })
    .catch(error => {
      aceleresStartupTabsPromise = null;
      throw error;
    });

  return aceleresStartupTabsPromise;
}

function ensureAceleresCriticalMenuData() {
  if (aceleresCriticalMenuDataPromise) {
    return aceleresCriticalMenuDataPromise;
  }

  aceleresCriticalMenuDataPromise = Promise.all([
    store.dispatch('agents/get'),
    store.dispatch('teams/get'),
    store.dispatch('labels/get'),
    store.dispatch('inboxes/get'),
    store.dispatch('customViews/get', 'conversation'),
    store.dispatch('customViews/get', 'contact'),
  ]).catch(error => {
    aceleresCriticalMenuDataPromise = null;
    throw error;
  });

  return aceleresCriticalMenuDataPromise;
}

function restoreCachedAceleresConversationList() {
  if (!isCacheableAceleresTab()) return false;

  const cacheKey = conversationViewCacheKey.value;
  // Concluídos/Arquivadas recebem mudanças de status vindas do funil. O
  // snapshot serve só para a primeira pintura e vale por pouco tempo; a
  // revalidação em segundo plano vem logo atrás (ver
  // ACELERES_HISTORY_CACHE_TTL_MS).
  if (
    isHistoryTab(activeAssigneeTab.value) &&
    !isFreshAceleresHistorySnapshot(cacheKey)
  ) {
    aceleresConversationCache.delete(cacheKey);
    aceleresHistoryCacheStamps.delete(cacheKey);
    return false;
  }
  const memoryConversations = aceleresConversationCache.get(cacheKey);
  const persistedSnapshot = Array.isArray(memoryConversations)
    ? null
    : readPersistedAceleresConversationList(cacheKey);
  let cachedConversations = Array.isArray(memoryConversations)
    ? memoryConversations
    : persistedSnapshot?.conversations;
  const cachedPage = Array.isArray(memoryConversations)
    ? aceleresConversationCachePages.get(cacheKey)
    : persistedSnapshot?.page;
  // Cache vazio nao e pre-carga: pode ter sido gravado durante uma troca de
  // aba enquanto a resposta anterior ainda estava chegando. Nesse caso a
  // caixa ativa precisa consultar o servidor novamente.
  if (!Array.isArray(cachedConversations) || !cachedConversations.length) {
    aceleresConversationCache.delete(cacheKey);
    aceleresCompleteConversationCacheKeys.delete(cacheKey);
    removePersistedAceleresConversationList(cacheKey);
    return false;
  }

  // Rede de segurança contra o cache ressuscitar uma conversa concluída.
  //
  // `SET_ALL_CONVERSATION` SUBSTITUI o registro que já está no store pelo que
  // vem no snapshot, então um snapshot gravado antes da conclusão devolve
  // `status: 'open'` por cima do `resolved` e o cartão reaparece na caixa
  // operacional — o sintoma "concluí, troquei de aba e ela voltou; só sai com
  // F5". A limpeza em `synchronizeConversationInAceleresCaches` cobre as
  // chaves que estão no Map em memória; as que existem SÓ no sessionStorage
  // (gravadas antes deste carregamento da página) não passam por lá.
  //
  // Aqui a decisão não depende de qual cache está desatualizado: o status
  // autoritativo é o do store, e uma caixa operacional nunca lista concluída.
  const resolvedStatus = wootConstants.STATUS_TYPE.RESOLVED;
  const conversationIsResolved = conversation => {
    const stored = getConversationById.value(conversation?.id);
    return (stored?.status || conversation?.status) === resolvedStatus;
  };
  if (isOperationalAceleresTab(activeAssigneeTab.value)) {
    const semConcluidas = cachedConversations.filter(
      conversation => !conversationIsResolved(conversation)
    );
    if (semConcluidas.length !== cachedConversations.length) {
      cachedConversations = semConcluidas;
      if (!cachedConversations.length) {
        aceleresConversationCache.delete(cacheKey);
        aceleresCompleteConversationCacheKeys.delete(cacheKey);
        removePersistedAceleresConversationList(cacheKey);
        return false;
      }
      persistAceleresConversationList(
        cacheKey,
        cachedConversations,
        cachedPage
      );
    }
  }

  aceleresConversationCache.set(cacheKey, cachedConversations);

  const selectedConversation = currentChat.value?.id ? currentChat.value : null;
  const selectedConversationIsCached = cachedConversations.some(
    conversation =>
      Number(conversation?.id) === Number(selectedConversation?.id)
  );
  const conversationsForStore =
    selectedConversation && !selectedConversationIsCached
      ? [...cachedConversations, selectedConversation]
      : cachedConversations;

  store.dispatch('mergeConversationPage', conversationsForStore);
  // O snapshot pode conter varias paginas ja roladas. Sem devolver o rastreio
  // de pagina junto com a lista, o proximo scroll pediria a pagina 1 outra vez
  // e a listagem ficava presa no tamanho do cache. `floor` no fallback mantem
  // uma pagina de sobreposicao: repetir conversas e inofensivo (o merge
  // deduplica), pular conversas nao.
  const restoredPage = Math.max(
    1,
    Number(cachedPage) ||
      Math.floor(cachedConversations.length / ACELERES_CONVERSATIONS_PER_PAGE)
  );
  if ((currentFiltersPage.value ?? 0) < restoredPage) {
    store.dispatch('conversationPage/setCurrentPage', {
      filter: currentPageFilterKey.value,
      page: restoredPage,
    });
  }
  chatsOnView.value = cachedConversations;
  return true;
}

function resetAceleresConversationListState({
  resetPagination = true,
  clearConversationStore = false,
} = {}) {
  aceleresLastLoadMoreKey.value = null;
  // Nas trocas de aba, a lista anterior não representa a nova visão. Limpar o
  // store evita mostrar uma mistura transitória enquanto a primeira página é
  // consultada; nos demais refreshes mantemos o comportamento sem flicker.
  if (clearConversationStore) {
    store.dispatch('emptyAllConversations', {
      preserveSelectedConversation: true,
    });
    chatsOnView.value = [];
  } else {
    chatsOnView.value = conversationList.value;
  }
  if (conversationListRef.value) {
    conversationListRef.value.scrollTop = 0;
  }
  if (resetPagination) {
    store.dispatch('conversationPage/reset');
  }
}

function buildConversationStatsCacheKey(params = {}) {
  return JSON.stringify({
    inboxId: params.inboxId || '',
    status: params.status || '',
    teamId: params.teamId || '',
    conversationType: params.conversationType || '',
    labels: Array.isArray(params.labels) ? [...params.labels].sort() : [],
    priority: params.priority || '',
    assigneeId: params.assigneeId || '',
    atendidoIa: params.atendidoIa || '',
  });
}

function fetchConversationStats({
  immediate = false,
  force = false,
  bypassServerCache = false,
} = {}) {
  const statsPayload = { ...conversationStatsFilters.value };

  const nextMetaKey = buildConversationStatsCacheKey(statsPayload);
  const statsUpdatedAt = new Date(
    conversationStats.value?.updatedOn || 0
  ).getTime();
  const hasFreshStats =
    statsUpdatedAt > 0 &&
    Date.now() - statsUpdatedAt < ACELERES_STATS_CACHE_TTL &&
    conversationStats.value?.metaKey === nextMetaKey;

  if (!immediate && !force && hasFreshStats) {
    return Promise.resolve();
  }

  return store.dispatch('conversationStats/get', {
    ...statsPayload,
    immediate,
    // `immediate` controla apenas o debounce do navegador. Dados realtime ja
    // sao aplicados otimisticamente; furar o cache do servidor em cada troca
    // de aba/reconexao fazia todos os agentes recalcularem as mesmas contagens.
    skip_cache: bypassServerCache,
  });
}

let realtimeStatsRefreshTimer = null;

function scheduleRealtimeStatsRefresh() {
  if (realtimeStatsRefreshTimer) {
    clearTimeout(realtimeStatsRefreshTimer);
  }

  realtimeStatsRefreshTimer = setTimeout(() => {
    realtimeStatsRefreshTimer = null;
    // `immediate: true` vira `skip_cache=true` no endpoint /conversations/meta,
    // que entao PULA o cache de 5s do servidor e recalcula as 12 contagens de
    // caixa na hora. Como esta funcao roda a cada mensagem que chega, cada
    // agente conectado disparava um recalculo completo por mensagem — medido
    // em 2 a 12 s contra 0,2 s no caminho com cache. Esta e uma reconciliacao
    // de fundo (a contagem local ja foi ajustada pelo evento do WebSocket),
    // entao pode e deve usar o cache; `force` mantem a requisicao acontecendo.
    fetchConversationStats({ force: true, immediate: false });
  }, 100);
}

function applyAceleresBoxCountDelta(delta = {}) {
  const currentCounts = conversationStats.value?.aceleresBoxCounts || {};
  const nextCounts = [
    'novos',
    'meus',
    'outros',
    'pessoais',
    'concluidos',
    'arquivadas',
  ].reduce((counts, key) => {
    const currentValue = Number(currentCounts[key] || 0);
    const nextValue = currentValue + Number(delta[key] || 0);
    counts[key] = Math.max(0, nextValue);
    return counts;
  }, {});

  store.dispatch('conversationStats/set', {
    aceleres_box_counts: nextCounts,
  });
}

function optimisticUnreadBoxForConversation(conversation) {
  if (!conversation || conversation.status === wootConstants.STATUS_TYPE.RESOLVED) {
    return null;
  }

  if (isPersonalConversationForCurrentUser(conversation)) return 'pessoais';

  const currentUserId = Number(currentUser.value?.id || 0);
  const assigneeId = Number(
    conversation?.meta?.assignee?.id || conversation?.assignee_id || 0
  );
  const started = isAceleresConversationStarted(conversation);

  if (assigneeId === currentUserId && currentUserId > 0) {
    return started ? 'meus' : 'novos';
  }

  // A fila sem responsável permanece em Novos mesmo após uma resposta.
  if (!assigneeId && (isManagerRole.value || isSupervisorUser.value)) {
    return 'novos';
  }

  if (
    assigneeId &&
    assigneeId !== currentUserId &&
    (isSupervisorUser.value || isManagerRole.value) &&
    (!supervisedTeamIds.value.length || supervisedSellerIds.value.includes(assigneeId))
  ) {
    return 'outros';
  }

  return null;
}

function adjustUnreadCountOptimistically({
  conversationId,
  previousUnreadCount,
  unreadCount,
  message,
  conversation: conversationPayload,
}) {
  if (
    !conversationStats.value?.aceleresBoxUnreadCounts ||
    (message &&
      (message.private === true || Number(message.message_type) !== 0))
  ) {
    return;
  }

  const conversation =
    getConversationById.value(conversationId) || conversationPayload;
  const box = optimisticUnreadBoxForConversation(conversation);
  if (!box) return;

  const wasUnread = Number(previousUnreadCount || 0) > 0;
  const isUnread = Number(unreadCount || 0) > 0;
  const delta = Number(isUnread) - Number(wasUnread);
  if (!delta) return;

  store.dispatch('conversationStats/adjustUnreadCounts', { box, delta });
}

function fetchFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  return store
    .dispatch('fetchFilteredConversations', {
      queryData: filterQueryGenerator(payload),
      page,
      sortBy: activeSortBy.value,
      aceleresBox: aceleresBoxForTab(activeAssigneeTab.value),
    })
    .then(emitConversationLoaded);

  showAdvancedFilters.value = false;
}

function fetchSavedFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  return store
    .dispatch('fetchFilteredConversations', {
      queryData: payload,
      page,
      sortBy: activeSortBy.value,
    })
    .then(emitConversationLoaded);
}

function onApplyFilter(payload) {
  payload = useSnakeCase(payload);
  resetBulkActions();
  foldersQuery.value = filterQueryGenerator(payload);
  resetAceleresConversationListState();
  fetchFilteredConversations(payload);
}

function closeAdvanceFiltersModal() {
  showAdvancedFilters.value = false;
  appliedFilter.value = [];
}

function onUpdateSavedFilter(payload, folderName) {
  const transformedPayload = useSnakeCase(payload);
  const payloadData = {
    ...unref(activeFolder),
    name: unref(folderName),
    query: filterQueryGenerator(transformedPayload),
  };
  store.dispatch('customViews/update', payloadData);
  closeAdvanceFiltersModal();
}

function onClickOpenAddFoldersModal() {
  showAddFoldersModal.value = true;
}

function onCloseAddFoldersModal() {
  showAddFoldersModal.value = false;
}

function onClickOpenDeleteFoldersModal() {
  showDeleteFoldersModal.value = true;
}

function onCloseDeleteFoldersModal() {
  showDeleteFoldersModal.value = false;
}

function setParamsForEditFolderModal() {
  // Here we are setting the params for edit folder modal to show the existing values.

  // For agent, team, inboxes,and campaigns we get only the id's from the query.
  // So we are mapping the id's to the actual values.

  // For labels we get the name of the label from the query.
  // If we delete the label from the label list then we will not be able to show the label name.

  // For custom attributes we get only attribute key.
  // So we are mapping it to find the input type of the attribute to show in the edit folder modal.
  return {
    agents: agentList.value,
    teams: teamsList.value,
    inboxes: inboxesList.value,
    labels: labels.value,
    campaigns: campaigns.value,
    languages: localizedLanguages.value,
    countries: localizedCountries.value,
    priority: [
      { id: 'low', name: t('CONVERSATION.PRIORITY.OPTIONS.LOW') },
      { id: 'medium', name: t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM') },
      { id: 'high', name: t('CONVERSATION.PRIORITY.OPTIONS.HIGH') },
      { id: 'urgent', name: t('CONVERSATION.PRIORITY.OPTIONS.URGENT') },
    ],
    filterTypes: advancedFilterTypes.value,
    allCustomAttributes: conversationCustomAttributes.value,
  };
}

function initializeExistingFilterToModal() {
  const statusFilter = initializeStatusAndAssigneeFilterToModal(
    activeStatus.value,
    currentUserDetails.value,
    activeAssigneeTab.value
  );
  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  if (statusFilter) {
    appliedFilter.value = [...appliedFilter.value, useCamelCase(statusFilter)];
  }

  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  const otherFilters = initializeInboxTeamAndLabelFilterToModal(
    props.conversationInbox,
    inbox.value,
    props.teamId,
    activeTeam.value,
    props.label
  ).map(useCamelCase);

  appliedFilter.value = [...appliedFilter.value, ...otherFilters];
}

function initializeFolderToFilterModal(newActiveFolder) {
  // Here we are setting the params for edit folder modal.
  //  To show the existing values. when we click on edit folder button.

  // Here we get the query from the active folder.
  // And we are mapping the query to the actual values.
  // To show in the edit folder modal by the help of generateValuesForEditCustomViews helper.
  const query = unref(newActiveFolder)?.query?.payload;
  if (!Array.isArray(query)) return;

  const newFilters = query.map(filter => {
    const transformed = useCamelCase(filter);
    const values = Array.isArray(transformed.values)
      ? generateValuesForEditCustomViews(
          useSnakeCase(filter),
          setParamsForEditFolderModal()
        )
      : [];

    return {
      attributeKey: transformed.attributeKey,
      attributeModel: transformed.attributeModel,
      customAttributeType: transformed.customAttributeType,
      filterOperator: transformed.filterOperator,
      queryOperator: transformed.queryOperator ?? 'and',
      values,
    };
  });

  appliedFilter.value = [...appliedFilter.value, ...newFilters];
}

function initalizeAppliedFiltersToModal() {
  appliedFilter.value = [...appliedFilters.value];
}

function onToggleAdvanceFiltersModal() {
  if (showAdvancedFilters.value === true) {
    closeAdvanceFiltersModal();
    return;
  }

  if (!hasAppliedFilters.value && !hasActiveFolders.value) {
    initializeExistingFilterToModal();
  }
  if (hasActiveFolders.value) {
    initializeFolderToFilterModal(activeFolder.value);
  }
  if (hasAppliedFilters.value) {
    initalizeAppliedFiltersToModal();
  }

  showAdvancedFilters.value = true;
}

// Aceleres: retry manual quando a lista falhou (listErrorStatus). Forca busca
// fresca ignorando o cache local, para o usuario nao depender de F5.
function retryFetchConversations() {
  return Promise.resolve(
    fetchConversations({ bypassCache: true, waitForStats: false })
  ).catch(() => null);
}

// O scroll infinito precisa da PROXIMA pagina da visao atual. Passar pelo
// caminho normal fazia o cache da aba (que existe sempre depois da primeira
// renderizacao) devolver o snapshot e recarregar a PAGINA 1 -- a listagem
// ficava travada nos itens mais recentes e o historico so aparecia por filtro.
// O sentinel é um IntersectionObserver: só emite quando o elemento CRUZA a
// borda. Se a página que chegou não acrescentou nada visível (todos os itens
// caem no filtro local da caixa), o sentinel continua à vista, nenhum evento
// novo acontece e a lista para de crescer mesmo havendo histórico. Nesses casos
// seguimos pedindo sozinhos, com teto para não varrer a conta inteira quando a
// caixa de fato acabou.
const MAX_PAGINAS_SEM_GANHO = 5;

// O refresh de fundo reescreve os filtros compartilhados com `page: 1` e chama
// `replaceConversationPage`, que avanca a sequencia de requisicoes da store.
// Se ele acontecer no meio de um append, a resposta da pagina seguinte e
// DESCARTADA pelos dois guards -- e como a pagina nunca avanca, o scroll pede
// a mesma pagina de novo, indefinidamente. Os dois caminhos passam a se
// excluir; quem perde a vez fica enfileirado.
const autoRefreshInFlight = ref(false);
// Refresh de lista pedido enquanto append ou outro refresh estava em voo.
// Drenada tanto pelo `finally` do refresh quanto pelo do append.
let autoRefreshQueued = false;
// Promessa do refresh em curso. O append espera por ela em vez de desistir:
// desistir deixava a lista parada até o usuário rolar de novo -- e como a
// chave anti-loop já tinha sido gravada com o mesmo estado, nem rolar
// resolvia. Numa conta com mensagens chegando (cada evento agenda um refresh)
// isso acontecia o tempo todo e parecia aleatório.
let aceleresRefreshPromise = null;

async function fetchNextConversationPage(paginasSeguidas = 0) {
  // Já existe um append em curso: ele continua sozinho pelo encadeamento do
  // fim desta função. Liberar a chave deixa o sentinel tentar outra vez caso
  // aquele termine sem avançar.
  if (aceleresAppendInFlight.value) {
    aceleresLastLoadMoreKey.value = null;
    return;
  }

  if (autoRefreshInFlight.value && aceleresRefreshPromise) {
    aceleresLastLoadMoreKey.value = null;
    await aceleresRefreshPromise;
    if (hasCurrentPageEndReached.value) return;
  }

  const requestedViewKey = conversationViewCacheKey.value;
  const previousLength = conversationList.value.length;
  store.dispatch('updateChatListFilters', conversationFilters.value);

  aceleresAppendInFlight.value = true;
  try {
    await store.dispatch('fetchAllConversations');
  } catch {
    return;
  } finally {
    aceleresAppendInFlight.value = false;
    // Um refresh de tempo real que chegou durante o append marcou a fila e
    // saiu. Antes, so o `finally` de OUTRO refresh drenava essa fila, entao um
    // pedido bloqueado por append morria ali: a conversa nova nunca entrava na
    // lista e so o contador subia. O timeout deixa o append terminar seu
    // pos-processamento antes de a lista ser mesclada.
    if (autoRefreshQueued && !autoRefreshInFlight.value) {
      autoRefreshQueued = false;
      setTimeout(() => {
        // eslint-disable-next-line no-use-before-define
        refreshConversationListInBackground({
          force: false,
          refreshStats: false,
          realtime: true,
        }).catch(() => {});
      }, 0);
    }
  }

  if (requestedViewKey !== conversationViewCacheKey.value) return;

  chatsOnView.value = conversationList.value;
  cacheCurrentAceleresConversationList();
  await nextTick();

  if (
    hasCurrentPageEndReached.value ||
    paginasSeguidas + 1 >= MAX_PAGINAS_SEM_GANHO
  ) {
    return;
  }

  const listElement = conversationListRef.value;
  const aindaNoFim = listElement
    ? listElement.scrollTop + listElement.clientHeight >=
      listElement.scrollHeight - ACELERES_LOAD_MORE_MARGIN
    : false;

  // Duas situações em que ninguém mais vai pedir a página seguinte: a lista
  // continua parada no fim (o sentinel só emite ao CRUZAR a borda, e ali ele
  // fica visível sem novo evento) ou a página que chegou não acrescentou nada
  // visível — todos os itens caíram no filtro local da caixa.
  if (aindaNoFim || conversationList.value.length === previousLength) {
    await fetchNextConversationPage(paginasSeguidas + 1);
  }
}

function fetchConversations({
  immediateStats = false,
  bypassCache = false,
  waitForStats = false,
  append = false,
} = {}) {
  if (append) {
    return fetchNextConversationPage();
  }

  const canUseAceleresCache =
    !hasAppliedFiltersOrActiveFolders.value &&
    !bypassCache;
  const restoredFromCache = canUseAceleresCache
    ? restoreCachedAceleresConversationList()
    : false;

  if (restoredFromCache) {
    // O snapshot aparece imediatamente; uma atualização eventual não deve
    // manter a transição visual aberta nem bloquear a troca de aba.
    aceleresViewTransitioning.value = false;
    const startupTabsRequest = aceleresStartupTabsReady.value
      ? Promise.resolve()
      : prefetchAceleresStartupTabs();
    // Keep warming the other tabs, but do not hold the transition open until
    // all of their message requests finish.
    startupTabsRequest.catch(() => null);
    if (
      !aceleresCompleteConversationCacheKeys.has(
        conversationViewCacheKey.value
      )
    ) {
      const refreshRequest = refreshConversationListInBackground({
        force: true,
      });
      if (!waitForStats) {
        refreshRequest.catch(() => null);
        return Promise.resolve();
      }
      return refreshRequest;
    }
    const statsRequest = fetchConversationStats({ immediate: immediateStats });
    if (!waitForStats) {
      statsRequest.catch(() => null);
      return Promise.resolve();
    }
    return statsRequest;
  }

  const tracksStartupPrefetch = startAceleresStartupPrefetch();
  store.dispatch('updateChatListFilters', conversationFilters.value);
  const statsRequest = fetchConversationStats({ immediate: immediateStats });
  const menuDataRequest = ensureAceleresCriticalMenuData();
  const conversationRequest = store
    .dispatch('fetchAllConversations')
    .then(emitConversationLoaded);
  const startupTabsRequest = Promise.all([conversationRequest, menuDataRequest]).then(
    () => prefetchAceleresStartupTabs()
  );
  // Secondary tabs are a background warm-up. The current tab becomes
  // interactive as soon as its own list and critical menu data are ready.
  startupTabsRequest.catch(() => null);
  return waitForZoraxyCriticalData({
    conversations: conversationRequest,
    stats: waitForStats ? statsRequest : Promise.resolve(),
    menuData: menuDataRequest,
  }).finally(() => {
    finishAceleresStartupPrefetch(tracksStartupPrefetch);
  });
}

function fetchAceleresView(options = {}) {
  const requestedViewKey = conversationViewCacheKey.value;
  aceleresViewTransitioning.value = true;

  return Promise.resolve(fetchConversations(options)).finally(async () => {
    if (requestedViewKey === conversationViewCacheKey.value) {
      // Uma troca rápida de aba/filtro pode invalidar a resposta que estava
      // em andamento. Nesse caso o request termina corretamente, mas nenhuma
      // conversa chega ao store e a tela fica vazia sem novo disparo. Refaz a
      // primeira página uma única vez para a visão atual.
      if (
        !conversationList.value.length &&
        !chatListLoading.value &&
        !chatListHasError.value
      ) {
        await refreshConversationListInBackground({
          force: true,
          refreshStats: false,
        });
      }

      aceleresViewTransitioning.value = false;
    }
  });
}

let realtimeConversationRefreshTimer = null;
let realtimeRefreshRetries = 0;
const REALTIME_REFRESH_MAX_RETRIES = 6;

// O contador da caixa e a lista nasciam do MESMO evento de mensagem, mas por
// caminhos com resiliencia diferente: `scheduleRealtimeStatsRefresh` nao tem
// guarda nenhuma (sempre recalcula no servidor), enquanto este refresh caia
// fora em silencio com a aba em segundo plano, com filtro aplicado ou com a
// lista carregando -- e nao havia retry periodico nenhum para cobrir o buraco.
// Resultado: o badge subia e a mensagem so aparecia quando outro gatilho
// (proxima mensagem com a aba visivel, reconexao, troca de caixa) forcava a
// busca. Aqui o caminho da lista passa a ter a mesma teimosia do contador.
function scheduleRealtimeConversationListRefresh({ retry = false } = {}) {
  if (!retry) {
    realtimeRefreshRetries = 0;
  }
  if (realtimeConversationRefreshTimer) {
    clearTimeout(realtimeConversationRefreshTimer);
  }

  realtimeConversationRefreshTimer = setTimeout(() => {
    realtimeConversationRefreshTimer = null;
    refreshConversationListInBackground({
      force: false,
      refreshStats: false,
      realtime: true,
    })
      .then(executou => {
        if (
          executou === false &&
          realtimeRefreshRetries < REALTIME_REFRESH_MAX_RETRIES
        ) {
          realtimeRefreshRetries += 1;
          scheduleRealtimeConversationListRefresh({ retry: true });
        }
      })
      .catch(() => {});
  }, 250);
}

let filteredRealtimeRefreshTimer = null;
let filteredRealtimeRefreshRetries = 0;

// Com filtro/pasta aplicados a consulta da lista e paginada por append
// (`currentFiltersPage + 1`), entao nao existe "refazer a pagina atual": o
// unico refresh honesto e reabrir a consulta do zero. Isso so vale a pena na
// primeira pagina; acima dela, refazer jogaria o usuario para o topo e
// apagaria todo o historico rolado, o que e pior que esperar.
//
// Os demais bloqueios sao TRANSITORIOS e agora reagendam em vez de desistir em
// silencio. Sem isto este caminho nao tinha rede de seguranca nenhuma: o
// polling periodico (`shouldRunChatListAutoRefresh`) tambem desiste quando ha
// pasta ou filtro aplicado, entao um refresh descartado aqui nunca voltava --
// a mensagem recebida so aparecia quando o usuario mexia na tela. E o caso
// comum do atendente, que trabalha com a caixa filtrada.
function scheduleFilteredRealtimeRefresh({ retry = false } = {}) {
  if (!retry) {
    filteredRealtimeRefreshRetries = 0;
  }
  if (filteredRealtimeRefreshTimer) {
    clearTimeout(filteredRealtimeRefreshTimer);
  }

  filteredRealtimeRefreshTimer = setTimeout(() => {
    filteredRealtimeRefreshTimer = null;
    if ((currentFiltersPage.value ?? 0) > 1) return;
    if (
      chatListLoading.value ||
      aceleresAppendInFlight.value ||
      autoRefreshInFlight.value
    ) {
      if (filteredRealtimeRefreshRetries < REALTIME_REFRESH_MAX_RETRIES) {
        filteredRealtimeRefreshRetries += 1;
        scheduleFilteredRealtimeRefresh({ retry: true });
      }
      return;
    }

    resetAceleresConversationListState();
    if (hasActiveFolders.value) {
      fetchSavedFilteredConversations(activeFolder.value.query);
    } else if (hasAppliedFilters.value) {
      fetchFilteredConversations(appliedFilters.value);
    }
  }, 1000);
}

// Devolve `false` quando desistiu por um bloqueio TRANSITORIO (a lista esta
// carregando): quem chamou pelo caminho de tempo real reagenda. Qualquer outra
// saida devolve `true`/undefined -- ou o pedido foi atendido, ou ficou
// enfileirado, ou foi delegado ao caminho filtrado.
async function refreshConversationListInBackground({
  force = false,
  refreshStats = true,
  realtime = false,
} = {}) {
  if (autoRefreshInFlight.value || aceleresAppendInFlight.value) {
    autoRefreshQueued = true;
    return true;
  }

  if (realtime && (hasActiveFolders.value || hasAppliedFilters.value)) {
    scheduleFilteredRealtimeRefresh();
    return true;
  }

  if (
    !shouldRunChatListAutoRefresh({
      hasActiveFolders: hasActiveFolders.value,
      hasAppliedFilters: hasAppliedFilters.value,
      isLoading: force ? false : chatListLoading.value,
      isRefreshing: false,
      // Aba oculta bloqueia o POLLING periodico (que existe so para cobrir
      // buraco), nunca um evento pontual que ja chegou pelo websocket: a
      // mensagem tem de estar na lista quando o usuario voltar para a aba.
      isHidden: realtime ? false : undefined,
    })
  ) {
    return !realtime || !chatListLoading.value;
  }

  let liberaEspera;
  aceleresRefreshPromise = new Promise(resolve => {
    liberaEspera = resolve;
  });
  autoRefreshInFlight.value = true;
  try {
    const requestedViewKey = conversationViewCacheKey.value;
    const requestedPageFilterKey = currentPageFilterKey.value;
    const requestedFilters = {
      ...conversationFilters.value,
      page: 1,
    };
    store.dispatch('updateChatListFilters', requestedFilters);
    const [conversations] = await Promise.all([
      store.dispatch('prefetchConversationList', requestedFilters),
      refreshStats
        ? fetchConversationStats({ immediate: force })
        : Promise.resolve(),
    ]);

    // `null` sinaliza falha/timeout do prefetch. Uma lista vazia (`[]`) e uma
    // resposta valida e deve substituir a pagina; a falha devolve `false` para
    // que o agendador de eventos realtime use o retry ja existente.
    if (!Array.isArray(conversations)) return false;

    if (requestedViewKey === conversationViewCacheKey.value) {
      if (force) {
        const synchronizedConversations = conversations;
        // O refresh busca somente a primeira página; a visão precisa voltar a
        // permitir paginação, mesmo que o cache anterior estivesse completo.
        aceleresCompleteConversationCacheKeys.delete(requestedViewKey);
        const loadedPage = currentFiltersPage.value ?? 0;

        if (loadedPage > 1) {
          // A visão já tem páginas trazidas pelo scroll. Trocá-la pela página 1
          // apagava todo o histórico rolado E zerava o rastreio de página, então
          // a lista voltava para as conversas recentes a cada atualização de
          // fundo. Aqui a primeira página manda no que ela cobre e as páginas já
          // roladas são preservadas, com a paginação intacta.
          //
          // Só mesclar não bastava: `mergeConversationPage` atualiza e insere,
          // nunca REMOVE. Uma conversa concluída em outro dispositivo (ou pelo
          // funil) some da resposta do servidor, mas o registro antigo — ainda
          // `open` — continuava no store, e a lista continuava mostrando o
          // cartão em Meus até um F5. Com o broadcast entregue isso não
          // aparece; quando ele se perde (aba oculta, reconexão de websocket,
          // fila represada) era permanente, e o refresh de segurança de cada
          // minuto não reparava nada.
          //
          // A regra é a que o próprio servidor garante: dentro da janela de
          // recência que a página 1 cobre, ela é a lista completa da caixa.
          // Fora dessa janela nada é descartado — é aí que moram as páginas
          // roladas. A fronteira ignora as fixadas, que o servidor devolve no
          // topo com qualquer data e rebaixariam o corte.
          const recencia = conversation =>
            Number(
              conversation?.last_activity_at ?? conversation?.timestamp ?? 0
            ) || 0;
          const recenciasNaoFixadas = synchronizedConversations
            .filter(conversation => !conversation?.is_pinned)
            .map(recencia);
          const idsDaPrimeiraPagina = new Set(
            synchronizedConversations.map(conversation =>
              Number(conversation?.id)
            )
          );
          const fronteira = recenciasNaoFixadas.length
            ? Math.min(...recenciasNaoFixadas)
            : null;
          const anteriores =
            fronteira === null
              ? conversationList.value || []
              : (conversationList.value || []).filter(
                  conversation =>
                    !idsDaPrimeiraPagina.has(Number(conversation?.id)) &&
                    recencia(conversation) < fronteira
                );

          await store.dispatch('replaceConversationPage', [
            ...synchronizedConversations,
            ...anteriores,
          ]);
          setCachedAceleresConversationList(
            requestedViewKey,
            conversationList.value || [],
            loadedPage
          );
        } else {
          // Refresh autoritativo: a página nova define a visão atual. Mesclar
          // itens antigos reintroduzia conversas fora do filtro/caixa e causava
          // o efeito de sumir e reaparecer entre recargas.
          setCachedAceleresConversationList(
            requestedViewKey,
            synchronizedConversations,
            1
          );
          await store.dispatch(
            'replaceConversationPage',
            synchronizedConversations
          );
          await store.dispatch('conversationPage/reset');
          await store.dispatch('conversationPage/setCurrentPage', {
            filter: requestedPageFilterKey,
            page: 1,
          });
          if (
            synchronizedConversations.length < ACELERES_CONVERSATIONS_PER_PAGE
          ) {
            await store.dispatch('conversationPage/setEndReached', {
              filter: requestedPageFilterKey,
            });
          }
        }
      } else {
        await store.dispatch('mergeConversationPage', conversations);
      }
      chatsOnView.value = conversationList.value;
      cacheCurrentAceleresConversationList();
    }
  } finally {
    autoRefreshInFlight.value = false;
    aceleresRefreshPromise = null;
    liberaEspera();
    if (autoRefreshQueued) {
      autoRefreshQueued = false;
      refreshConversationListInBackground({ force: false }).catch(() => {});
    }
  }

  // Um refresh que caiu no meio de uma rolagem consome o evento do sentinel
  // (ele só emite ao CRUZAR a borda). Se a lista continua encostada no fim,
  // ninguém mais pediria a página seguinte.
  await nextTick();
  onConversationListScroll();
}

function resetAndFetchData({ bypassCache = false } = {}) {
  aceleresFilterApplied.value = false;
  appliedIaFilter.value = false;
  aceleresDropdownFilterRef.value?.clearAll(false);
  appliedFilter.value = [];
  resetBulkActions();
  resetAceleresConversationListState();
  store.dispatch('clearConversationFilters');
  if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  }
  if (props.foldersId) {
    return;
  }
  fetchConversations({
    immediateStats: true,
    bypassCache,
    // A lista deve ficar interativa assim que a primeira página responder;
    // os contadores continuam sendo atualizados pelo endpoint /meta.
    waitForStats: false,
  });
}

// Aceleres: o IntersectionObserver do sentinel remonta a cada toggle do spinner
// e pode redisparar em rajada. A trava por chave abaixo cobre so o caminho sem
// filtro; este throttle protege tambem os caminhos de pasta/filtro salvo.
let aceleresLastLoadMoreAt = 0;
const LOAD_MORE_THROTTLE_MS = 300;

function loadMoreConversations() {
  if (hasCurrentPageEndReached.value || chatListLoading.value) {
    return;
  }

  const now = Date.now();
  if (now - aceleresLastLoadMoreAt < LOAD_MORE_THROTTLE_MS) return;
  aceleresLastLoadMoreAt = now;

  if (!hasAppliedFiltersOrActiveFolders.value) {
    if (hasCompleteCachedAceleresView.value) {
      return;
    }

    // O fim da lista e declarado pelo servidor (pagina vazia -> setEndReached),
    // nunca pelo contador da aba: o badge conta somente conversas `open`,
    // enquanto a caixa lista tambem `pending`/`snoozed`, e qualquer divergencia
    // entre os dois virava um teto artificial no meio do historico.
    // Aceleres: trava anti-loop. O sentinel (IntersectionObserver) remonta a
    // cada toggle do spinner e redispara o observed; quando a lista local fica
    // vazia (filtro client-side) a pagina pedida nunca avanca e o payload nunca
    // vem vazio, entao sem esta trava o fetch se repete indefinidamente.
    const key = JSON.stringify([
      conversationViewCacheKey.value,
      conversationListPagination.value,
      conversationList.value.length,
    ]);
    if (key === aceleresLastLoadMoreKey.value) return;
    aceleresLastLoadMoreKey.value = key;
    fetchConversations({ append: true });
  } else if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  } else if (hasAppliedFilters.value) {
    fetchFilteredConversations(appliedFilters.value);
  }
}

// O sentinel é um IntersectionObserver e só avisa quando o elemento CRUZA a
// borda: parado no fim da lista ele fica visível e nunca mais emite. O evento
// nativo do contêiner cobre esse caso (e o scroll programático, que o
// Virtualizer não repassa). O guard de chave em loadMoreConversations impede
// que os dois caminhos peçam a mesma página duas vezes.
function onConversationListScroll() {
  const listElement = conversationListRef.value;
  if (!listElement) return;

  const noFim =
    listElement.scrollTop + listElement.clientHeight >=
    listElement.scrollHeight - ACELERES_LOAD_MORE_MARGIN;
  if (!noFim) return;

  loadMoreConversations();
}

// Use IntersectionObserver instead of @scroll since Virtualizer only emits on user scroll.
// If the list doesn’t fill the viewport, loading can stall.
// IntersectionObserver triggers as soon as the sentinel is visible.
const intersectionObserverOptions = computed(() => ({
  root: conversationListRef.value,
  rootMargin: '100px 0px 100px 0px',
}));

function updateAssigneeTab(
  selectedTab,
  { updateRouteQuery = true, fetchData = true } = {}
) {
  if (
    selectedTab === wootConstants.ASSIGNEE_TYPE.PERSONAL &&
    !shouldShowPersonalTab.value
  ) {
    return updateAssigneeTab('novos', { updateRouteQuery });
  }

  if (activeAssigneeTab.value !== selectedTab) {
    if (
      !aceleresCompleteConversationCacheKeys.has(conversationViewCacheKey.value)
    ) {
      cacheCurrentAceleresConversationList();
    }
    aceleresUserPickedTab.value = true;
    resetBulkActions();
    emitter.emit('clearSearchInput');
    if (isHistoryTab(selectedTab) && !isHistoryTab(activeAssigneeTab.value)) {
      previousStatusBeforeConcludedTab.value = activeStatus.value;
      previousAssigneeTabBeforeHistory.value = activeAssigneeTab.value;
      activeStatus.value = wootConstants.STATUS_TYPE.RESOLVED;
    } else if (
      !isHistoryTab(selectedTab) &&
      isHistoryTab(activeAssigneeTab.value)
    ) {
      activeStatus.value =
        previousStatusBeforeConcludedTab.value ||
        wootConstants.STATUS_TYPE.OPEN;
    }
    activeAssigneeTab.value = selectedTab;
    if (updateRouteQuery) {
      setAceleresBoxQuery(selectedTab);
    }
    // Trocar de aba precisa mostrar uma única visão atualizada. Não restaurar
    // um snapshot antigo evita o efeito de carregar uma lista e substituí-la
    // logo depois por outra resposta.
    resetAceleresConversationListState({
      resetPagination: true,
      clearConversationStore: false,
    });
    if (!fetchData) {
      return Promise.resolve();
    }

    return fetchAceleresView({
      immediateStats: false,
      bypassCache: false,
      // Os contadores continuam atualizando em segundo plano; a troca de
      // aba não deve aguardar o endpoint de meta para mostrar a lista.
      waitForStats: false,
    });
  }

  if (updateRouteQuery) {
    setAceleresBoxQuery(selectedTab);
  }

  return Promise.resolve();
}

function toggleConcludedTab() {
  const nextTab =
    activeAssigneeTab.value === CONCLUDED_ASSIGNEE_TAB
      ? previousAssigneeTabBeforeHistory.value
      : CONCLUDED_ASSIGNEE_TAB;

  return updateAssigneeTab(nextTab);
}

async function showMineTabAfterConversationStart({
  conversationId,
  refreshConversation = true,
  conversation = null,
} = {}) {
  let conversationToKeep = conversation;
  if (conversationId && refreshConversation) {
    conversationToKeep =
      (await store.dispatch('getConversation', conversationId)) ||
      conversationToKeep;
  }

  const keepStartedConversationVisible = () => {
    if (!conversationToKeep) return;
    store.dispatch('addConversation', conversationToKeep);
    store.dispatch('updateConversation', conversationToKeep);
  };

  // Ver `aceleresForceServerListRefresh`: a primeira lista depois de assumir
  // tem de vir do banco, nunca do cache de 30s do endpoint.
  aceleresForceServerListRefresh.value = true;
  try {
    if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ME) {
      resetAceleresConversationListState();
      // `bypassCache` pula tambem o snapshot local (que descreve Meus sem esta
      // conversa) e, de quebra, tira `fetchConversations` do atalho que devolve
      // `Promise.resolve()` na hora: pelo caminho completo o await so solta
      // quando a resposta ja esta no store. Sem isso o refresh de fundo pousava
      // DEPOIS de `keepStartedConversationVisible` e desfazia a reinsercao.
      await fetchConversations({
        immediateStats: true,
        bypassCache: true,
        waitForStats: true,
      });
    } else {
      await updateAssigneeTab(wootConstants.ASSIGNEE_TYPE.ME, {
        immediateStats: true,
        updateRouteQuery: false,
      });
    }
  } finally {
    // O refresh de fundo dessa troca de aba ja copiou `conversationFilters`
    // (com skipCache) antes deste ponto, entao baixar a bandeira aqui nao o
    // devolve ao cache.
    aceleresForceServerListRefresh.value = false;
  }

  keepStartedConversationVisible();
}

// Fim do fluxo de "iniciar nova conversa".
//
// Conversa aberta pela instância pessoal nasce na caixa Pessoais, não em Meus:
// sem trocar a aba, o vendedor cai no chat e a lista ao lado continua mostrando
// outra caixa, onde a conversa que ele acabou de criar não existe.
function onStartConversationFlowStarted(payload = {}) {
  closeQuickSearch();

  if (!payload?.personal || !shouldShowPersonalTab.value) return;

  updateAssigneeTab(wootConstants.ASSIGNEE_TYPE.PERSONAL, {
    updateRouteQuery: false,
  });
}

function updateUnreadSubfilter(nextValue) {
  if (!shouldShowUnreadSubfilters.value) {
    return;
  }

  if (activeUnreadSubfilter.value === nextValue) {
    return;
  }

  const previousCacheKey = conversationViewCacheKey.value;

  unreadSubfiltersByTab.value = {
    ...unreadSubfiltersByTab.value,
    [activeAssigneeTab.value]: nextValue,
  };

  const nextCacheKey = conversationViewCacheKey.value;
  [previousCacheKey, nextCacheKey].forEach(cacheKey => {
    aceleresConversationCache.delete(cacheKey);
    aceleresCompleteConversationCacheKeys.delete(cacheKey);
  });
  aceleresConversationCacheVersion.value += 1;

  resetBulkActions();
  resetAceleresConversationListState({ clearConversationStore: true });

  // A troca de subfiltro precisa consultar a primeira página do filtro novo.
  // Apenas recalcular a lista local deixa conversas fora do snapshot atual e
  // fazia o sentinel declarar "todas carregadas" prematuramente.
  return fetchAceleresView({ immediateStats: true, bypassCache: true });
}

function onSortChange(value) {
  if (activeSortBy.value === value) return;

  activeSortBy.value = value;
  store.dispatch('setChatSortFilter', value);
  resetBulkActions();
  resetAceleresConversationListState();

  if (hasActiveFolders.value) {
    fetchSavedFilteredConversations(activeFolder.value.query);
  } else if (hasAppliedFilters.value) {
    fetchFilteredConversations(appliedFilters.value);
  } else {
    fetchConversations();
  }
}

function onBasicFilterChange(value, type) {
  if (type === 'status') {
    activeStatus.value = value;
  } else if (type === 'inbox') {
    activeInboxFilter.value = value;
  } else {
    activeSortBy.value = value;
  }
  resetAndFetchData();
}

function onInboxFilterChange(inboxId) {
  activeInboxFilter.value = inboxId;
  resetAndFetchData();
}

function onAceleresFilterApply(conditions) {
  if (!conditions || conditions.length === 0) {
    onAceleresFilterClear();
    return;
  }
  aceleresFilterApplied.value = true;
  activeAceleresFilterConditions.value = conditions;
  appliedIaFilter.value = conditions.some(
    c => c.attributeKey === 'atendido_ia' || c.attribute_key === 'atendido_ia'
  );
  const snaked = useSnakeCase(JSON.parse(JSON.stringify(conditions)));
  store.dispatch('setConversationFilters', snaked);
  resetBulkActions();
  resetAceleresConversationListState();
  store
    .dispatch('fetchFilteredConversations', {
      queryData: filterQueryGenerator(snaked),
      page: 1,
      sortBy: activeSortBy.value,
      aceleresBox: aceleresBoxForTab(activeAssigneeTab.value),
    })
    .then(emitConversationLoaded);
  fetchConversationStats();
}

function onAceleresFilterClear() {
  activeAceleresFilterConditions.value = [];
  resetAndFetchData();
}

function toggleIaFilter() {
  iaOnly.value = !iaOnly.value;
  resetAndFetchData();
}

function onQfStatusChange(status) {
  activeStatus.value = status || wootConstants.STATUS_TYPE.OPEN;
  store.dispatch('setChatStatusFilter', activeStatus.value);
  resetAndFetchData();
}

function onQfLabelChange(labelSlug) {
  qfLabelSlug.value = labelSlug;
  resetAndFetchData();
}

function onQfTeamChange(teamId) {
  qfTeamId.value = teamId;
  resetAndFetchData();
}

function onQfPriorityChange(priority) {
  qfPriority.value = priority;
  resetAndFetchData();
}

function onQfAgentChange(agentId) {
  qfAgentId.value = agentId;
  resetAndFetchData();
}

function clearQfFilters() {
  activeInboxFilter.value = null;
  activeStatus.value = wootConstants.STATUS_TYPE.OPEN;
  store.dispatch('setChatStatusFilter', activeStatus.value);
  qfPriority.value = null;
  qfLabelSlug.value = null;
  qfAgentId.value = null;
  qfTeamId.value = null;
  resetAndFetchData();
}
function openLastSavedItemInFolder() {
  const lastItemOfFolder = folders.value[folders.value.length - 1];
  const lastItemId = lastItemOfFolder.id;
  router.push({
    name: 'folder_conversations',
    params: { id: lastItemId },
  });
}

function openLastItemAfterDeleteInFolder() {
  if (folders.value.length > 0) {
    openLastSavedItemInFolder();
  } else {
    router.push({ name: 'home' });
    fetchConversations();
  }
}

function redirectToConversationList() {
  const {
    params: { accountId, inbox_id: inboxId, label, teamId },
    name,
  } = route;

  let conversationType = '';
  if (isOnMentionsView({ route: { name } })) {
    conversationType = wootConstants.CONVERSATION_TYPE.MENTION;
  } else if (isOnParticipatingView({ route: { name } })) {
    conversationType = wootConstants.CONVERSATION_TYPE.PARTICIPATING;
  } else if (isOnUnattendedView({ route: { name } })) {
    conversationType = wootConstants.CONVERSATION_TYPE.UNATTENDED;
  }
  router.push(
    conversationListPageURL({
      accountId,
      conversationType: conversationType,
      customViewId: props.foldersId,
      inboxId,
      label,
      teamId,
    })
  );
}

async function assignPriority(priority, conversationId = null) {
  store.dispatch('setCurrentChatPriority', {
    priority,
    conversationId,
  });
  store.dispatch('assignPriority', { conversationId, priority }).then(() => {
    useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
      newValue: priority,
      from: 'Context menu',
    });
    useAlert(
      t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
        priority,
        conversationId,
      })
    );
  });
}

async function markAsUnread(conversationId) {
  try {
    await store.dispatch('markMessagesUnread', {
      id: conversationId,
    });
    redirectToConversationList();
  } catch (error) {
    // Ignore error
  }
}

// Aplica o estado de fixação em todas as superfícies locais: o store (que
// reordena a lista, porque `sortComparatorWithPinned` põe as fixadas no topo),
// a referência que a lista visível está renderizando e os snapshots do cache
// Aceleres. Usada tanto para o palpite otimista quanto para a confirmação do
// servidor e para a reversão em caso de erro.
function applyPinStateLocally(conversationId, isPinned, pinnedAt) {
  const normalizedConversationId = Number(conversationId);
  if (!normalizedConversationId) return;

  store.dispatch('setConversationPin', {
    conversationId: normalizedConversationId,
    isPinned,
    pinnedAt,
  });

  // A lista Aceleres pode estar renderizando uma referencia do cache local,
  // diferente da instancia atualizada pela mutation do store. Sincronizar a
  // referencia visivel evita que o pin apareca na API mas desapareca do card.
  const visibleConversation = (conversationList.value || []).find(
    conversation => Number(conversation?.id) === normalizedConversationId
  );
  if (visibleConversation) {
    visibleConversation.is_pinned = Boolean(isPinned);
    visibleConversation.pinned_at = pinnedAt || null;
  }

  synchronizeConversationInAceleresCaches(
    {
      id: normalizedConversationId,
      is_pinned: Boolean(isPinned),
      pinned_at: pinnedAt || null,
    },
    { preservePinned: false }
  );
}

// `allowUndo` existe para o proprio "Desfazer" do toast: ele chama togglePin
// de volta, e sem isso cada desfazer ofereceria outro desfazer, sem fim.
async function togglePin(conversationId, { allowUndo = true } = {}) {
  const normalizedConversationId = Number(conversationId);
  const currentConversation =
    (conversationList.value || []).find(
      conversation => Number(conversation?.id) === normalizedConversationId
    ) || store.getters.getConversationById(normalizedConversationId);
  const previousPinned = Boolean(currentConversation?.is_pinned);
  const previousPinnedAt = currentConversation?.pinned_at || null;
  const optimisticPinned = !previousPinned;

  // Fixar além do limite volta em 422. Quando as 3 fixadas já estão à vista,
  // o cartão não chega a subir: sem isto ele pulava para o topo e caía de
  // volta quando a resposta chegasse, junto com o modal do limite.
  const visiblePinnedCount = (conversationList.value || []).filter(
    conversation => Boolean(conversation?.is_pinned)
  ).length;
  const willHitPinLimit =
    optimisticPinned && visiblePinnedCount >= PINNED_CONVERSATION_LIMIT;

  // O clique reordena a lista na hora; o POST só confirma. Sem isto o cartão
  // ficava fixado (ou desafixado) até a resposta chegar — medido entre 64ms e
  // 2,1s em homologação, com o usuário achando que o clique não pegou.
  if (willHitPinLimit) {
    showPinLimitModal.value = true;
  } else {
    applyPinStateLocally(
      normalizedConversationId,
      optimisticPinned,
      optimisticPinned ? new Date().toISOString() : null
    );
  }

  try {
    const data = await store.dispatch('togglePin', conversationId);
    // O servidor é quem manda: se o palpite do limite estava errado (uma
    // fixada foi solta em outra caixa, ou o cache estava velho), o pin passa
    // e o modal sai da frente.
    showPinLimitModal.value = false;
    applyPinStateLocally(
      Number(data.id || conversationId),
      Boolean(data.is_pinned),
      data.pinned_at || null
    );
    useAlert(
      data?.is_pinned ? 'Conversa fixada no topo.' : 'Conversa desafixada.',
      allowUndo
        ? {
            type: 'button',
            message: 'Desfazer',
            // O toast padrao dura 2,5s — pouco para ler e decidir desfazer.
            duration: 6000,
            callback: () =>
              togglePin(normalizedConversationId, { allowUndo: false }),
          }
        : null
    );
  } catch (error) {
    // O palpite otimista volta atrás; nada a desfazer quando ele nem chegou a
    // ser aplicado (caminho do limite).
    if (!willHitPinLimit) {
      applyPinStateLocally(
        normalizedConversationId,
        previousPinned,
        previousPinnedAt
      );
    }

    // 422 é o limite de 3 fixadas — vai no modal, não no toast.
    if (error?.response?.status === 422) {
      showPinLimitModal.value = true;
      return;
    }

    useAlert(
      error?.response?.data?.message ||
        'Não foi possível alterar a fixação da conversa.'
    );
  }
}

async function markAsRead(conversationId) {
  try {
    await store.dispatch('markMessagesRead', {
      id: conversationId,
    });
  } catch (error) {
    // Ignore error
  }
}

async function onAssignTeam(team, conversationId = null) {
  try {
    await store.dispatch('assignTeam', {
      conversationId,
      teamId: team.id,
    });
    useAlert(
      t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.SUCCESFUL', {
        team: team.name,
        conversationId,
      })
    );
  } catch (error) {
    useAlert(t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.FAILED'));
  }
}

async function onAssignPersonal(conversationId) {
  applyAceleresBoxCountDelta({ meus: -1, pessoais: 1 });
  try {
    await store.dispatch('assignAgent', {
      conversationId,
      agentId: null,
      personal: true,
    });
  } catch (error) {
    applyAceleresBoxCountDelta({ meus: 1, pessoais: -1 });
    useAlert(
      t('CONVERSATION.CARD_CONTEXT_MENU.API.PERSONAL_ASSIGNMENT.FAILED')
    );
  }
}

async function onStartConversation(conversationId) {
  try {
    const response = await AceleresTeamPresenceAPI.lockConversation({
      conversationDisplayId: conversationId,
    });
    const conversation = getConversationById.value(conversationId);
    const currentUserData = currentUser.value || {};
    const assignee = response?.data?.assignee || {
      id: currentUserData.id,
      name: currentUserData.name,
      available_name: currentUserData.available_name,
      email: currentUserData.email,
      thumbnail: currentUserData.thumbnail,
    };
    let startedConversation = null;
    const startedAttributes = {
      rodizio_locked: true,
      attendance_started: true,
      rodizio_locked_by: currentUserData.id,
      rodizio_locked_reason: 'iniciar_button',
      rodizio_locked_at: new Date().toISOString(),
    };
    // Mesmo escudo do ReplyBox: listagem em voo não pode desfazer o início.
    store.dispatch('markConversationAttendanceStarted', {
      conversationId,
      assignee,
      attributes: startedAttributes,
    });

    if (conversation) {
      const currentUpdatedAt = Number(conversation.updated_at) || 0;
      startedConversation = {
        ...conversation,
        updated_at: Math.max(
          currentUpdatedAt + 1,
          Math.floor(Date.now() / 1000)
        ),
        meta: {
          ...(conversation.meta || {}),
          assignee,
        },
        additional_attributes: {
          ...(conversation.additional_attributes || {}),
          ...(response?.data?.additional_attributes || {}),
          ...startedAttributes,
        },
      };
      await store.dispatch('updateConversation', startedConversation);
    }

    const fetchedConversation = await store.dispatch(
      'getConversation',
      conversationId
    );
    synchronizeConversationInAceleresCaches(
      fetchedConversation || startedConversation
    );
    applyAceleresBoxCountDelta({ novos: -1, meus: 1 });
    await showMineTabAfterConversationStart({
      conversationId,
      refreshConversation: false,
      conversation: fetchedConversation || startedConversation,
    });
    useAlert('Atendimento iniciado e movido para Meus.');
  } catch (error) {
    useAlert('Não foi possível iniciar o atendimento. Tente novamente.');
  }
}

// "Mover para meus" só é oferecido dentro da caixa Pessoais (ver canMoveToMine
// no contextMenu), então o destino é sempre Meus — nunca Novos.
//
// Até 17/08/2026 a conversa pessoal tomava um desvio: `start: false` e troca de
// aba para Novos. O desvio não podia funcionar por duas razões independentes —
// `novos_mine_scope` exclui conversa pessoal, e em inbox individual_own o
// AssignmentService devolvia a atribuição para pessoal. Resultado: a conversa
// ficava parada em Pessoais e o vendedor era levado para uma caixa vazia.
//
// `moveConversationToMine` manda `start: true`, que é o que marca o atendimento
// como iniciado e faz a conversa entrar em `meus_scope` — e também o que libera
// o envio no Aceleres::OutboundSendGate.
async function onAssignMine(conversationId) {
  applyAceleresBoxCountDelta({ meus: 1, pessoais: -1 });
  try {
    const movedConversation = await store.dispatch('moveConversationToMine', {
      conversationId,
    });
    synchronizeConversationInAceleresCaches(movedConversation);
    // Marca lida ao assumir. Ate 04/09/2026 esta era a UNICA leitura que valia
    // para uma conversa em Pessoais: abrir a conversa nao marcava lida (guard
    // isAutoReadBlocked, removido de messageReadActions nesse dia a pedido do
    // produto). Hoje abrir ja marca, e isto cobre quem assume sem abrir.
    markAsRead(conversationId);
    await showMineTabAfterConversationStart({
      conversationId,
      refreshConversation: false,
      conversation: movedConversation,
    });
  } catch (error) {
    applyAceleresBoxCountDelta({ meus: -1, pessoais: 1 });
    useAlert('Não foi possível mover a conversa para meus. Tente novamente.');
  }
}

// Mesma acao pedida pelo menu do cabecalho da conversa (MoreActions), que nao
// e descendente da ChatList e por isso nao alcanca o `provide`.
function onMoveToMineRequested(payload = {}) {
  const conversationId = payload?.conversationId;
  if (!conversationId) return;
  onAssignMine(conversationId);
}

async function onAssignSelf(conversationId) {
  try {
    const conversation = getConversationById.value(conversationId);
    if (!conversation?.inbox_id) return;
    const listedStatus = String(
      conversation.inbox_connection_status || ''
    ).toLowerCase();
    if (
      listedStatus !== 'connected' &&
      (listedStatus || Number(conversation.inbox_instance_id || 0) > 0)
    ) {
      useAlert(
        'Não é possível atribuir este lead enquanto a conexão do WhatsApp não estiver confirmada.'
      );
      return;
    }
    const inboxId = Number(conversation?.inbox_id || 0);
    if (inboxId > 0) {
      const cachedInbox = store.getters['inboxes/getInbox'](inboxId);
      const verifiedInbox = await store.dispatch('inboxes/fetch', inboxId);
      const currentInbox = verifiedInbox || cachedInbox;
      const inboxAttributes = currentInbox?.additional_attributes || {};
      const isAPIInbox =
        conversation?.meta?.channel === 'Channel::Api' ||
        currentInbox?.channel_type === 'Channel::Api';
      const isWameInbox = Number(inboxAttributes.aceleres_instance_id || 0) > 0;
      const connectionStatus = String(
        inboxAttributes.connection_status || ''
      ).toLowerCase();
      const connectionNotReady = isWameInbox
        ? connectionStatus !== 'connected'
        : Boolean(connectionStatus) && connectionStatus !== 'connected';

      if (
        (isAPIInbox && !verifiedInbox) ||
        connectionNotReady
      ) {
        useAlert(
          'Não é possível atribuir este lead enquanto a conexão do WhatsApp não estiver confirmada.'
        );
        return;
      }
    }

    await store.dispatch('assignAgent', {
      conversationId,
      agentId: currentUser.value?.id,
      start: false,
    });
    await store.dispatch('getConversation', conversationId);
    useAlert('Lead atribuído a você e mantido em Novos.');
  } catch (error) {
    useAlert('Não foi possível atribuir o lead a você. Tente novamente.');
  }
}

function shouldRouteUnassignToPersonal(conversationId) {
  const conversation = getConversationById.value(conversationId);
  if (!conversation) {
    return false;
  }

  const currentUserId = Number(currentUser.value?.id);
  const personalOwnerId = Number(
    conversation.additional_attributes?.personal_owner_chatwoot_user_id
  );
  const isOwnedPersonalInbox = ownedPersonalInboxIds.value.includes(
    Number(conversation.inbox_id)
  );

  return (
    isOwnedPersonalInbox ||
    personalOwnerId === currentUserId
  );
}

async function onAssignAgent(agent, conversationIds = null, options = {}) {
  const ids = Array.isArray(conversationIds)
    ? conversationIds
    : conversationIds
      ? [conversationIds]
      : [];

  if (
    agent?.id == null &&
    ids.length === 1 &&
    shouldRouteUnassignToPersonal(ids[0])
  ) {
    await onAssignPersonal(ids[0]);
    return;
  }

  if (
    Number(agent?.id) === Number(currentUser.value?.id) &&
    ids.length === 1 &&
    shouldRouteUnassignToPersonal(ids[0])
  ) {
    await onAssignMine(ids[0]);
    return;
  }

  await bulkAssignAgent(agent, conversationIds, options);
}

async function toggleConversationStatus(
  conversationId,
  status,
  snoozedUntil,
  customAttributes = null
) {
  const payload = {
    conversationId,
    status,
    snoozedUntil,
  };

  // Aceleres: concluir pede o tipo de conclusão (depois do modal de atributos
  // obrigatórios, quando ele existir); cancelar não conclui.
  if (status === wootConstants.STATUS_TYPE.RESOLVED) {
    const conclusionResult = await requestConclusionResult();
    if (!conclusionResult) return;
    payload.conclusionResult = conclusionResult;
  }

  if (customAttributes) {
    payload.customAttributes = customAttributes;
  }

  store.dispatch('toggleStatus', payload).then(() => {
    useAlert(t('CONVERSATION.CHANGE_STATUS'));
  });
}

function handleResolveConversation(conversationId, status, snoozedUntil) {
  if (status !== wootConstants.STATUS_TYPE.RESOLVED) {
    toggleConversationStatus(conversationId, status, snoozedUntil);
    return;
  }

  // Check for required attributes before resolving
  const conversation = getConversationById.value(conversationId);
  const currentCustomAttributes = conversation?.custom_attributes || {};
  const { hasMissing, missing } = checkMissingAttributes(
    currentCustomAttributes
  );

  if (hasMissing) {
    // Pass conversation context through the modal's API
    const conversationContext = {
      id: conversationId,
      snoozedUntil,
    };
    resolveAttributesModalRef.value?.open(
      missing,
      currentCustomAttributes,
      conversationContext
    );
  } else {
    toggleConversationStatus(conversationId, status, snoozedUntil);
  }
}

function handleResolveWithAttributes({ attributes, context }) {
  if (context) {
    const existingConversation = getConversationById.value(context.id);
    const currentCustomAttributes =
      existingConversation?.custom_attributes || {};
    const mergedAttributes = { ...currentCustomAttributes, ...attributes };

    toggleConversationStatus(
      context.id,
      wootConstants.STATUS_TYPE.RESOLVED,
      context.snoozedUntil,
      mergedAttributes
    );
  }
}

function allSelectedConversationsStatus(status) {
  if (!selectedConversations.value.length) return false;
  return selectedConversations.value.every(item => {
    return getConversationById.value(item)?.status === status;
  });
}

function onContextMenuToggle(state) {
  isContextMenuOpen.value = state;
}

function toggleSelectAll(check) {
  selectAllConversations(check, conversationList);
}

useEmitter(
  'fetch_conversation_stats',
  ({ force = true, immediate = false } = {}) => {
    // A leitura continua imediata; mensagens novas usam a atualização local
    // do WebSocket e uma consulta de meta com debounce para não bloquear a
    // fila de chegada em períodos de alto volume.
    //
    // `immediate` só chega aqui pela leitura (ou "marcar como não lida")
    // explícita de uma conversa, e essa reconciliação não pode ser pulada por
    // causa de um filtro na tela: era assim que, com filtro ou pasta aplicada,
    // ler uma conversa não atualizava badge nenhum até o refresh periódico.
    if (immediate) {
      fetchConversationStats({ force, immediate });
      return;
    }

    if (
      hasActiveFolders.value ||
      (hasAppliedFilters.value && !aceleresFilterApplied.value)
    )
      return;

    scheduleRealtimeStatsRefresh();
  }
);

useEmitter(
  'conversation_realtime_message',
  ({
    conversationId,
    lastActivityAt,
    unreadCount,
    message,
    previousUnreadCount,
    conversation: conversationPayload,
  } = {}) => {
    const normalizedConversationId = Number(conversationId);
    if (!normalizedConversationId) return;

    // Atualiza snapshots já aquecidos para que uma troca de caixa não volte a
    // mostrar a versão anterior da conversa.
    synchronizeConversationInAceleresCaches({
      id: normalizedConversationId,
      last_activity_at: lastActivityAt,
      timestamp: lastActivityAt,
      unread_count: unreadCount,
      message,
    });
    adjustUnreadCountOptimistically({
      conversationId: normalizedConversationId,
      previousUnreadCount,
      unreadCount,
      message,
      conversation: conversationPayload,
    });

    // Mensagens, preview, unread e ordenação já foram atualizados localmente
    // pelo store quando a conversa já está visível. A existência no store não
    // basta: ela pode pertencer a outra aba/página e ainda não estar na lista
    // atual. Nesse caso, o refresh busca a conversa e reposiciona a primeira
    // página para que ela apareça.
    const isConversationInVisibleList = conversationList.value.some(
      conversation => Number(conversation?.id) === normalizedConversationId
    );
    if (!isConversationInVisibleList) {
      scheduleRealtimeConversationListRefresh();
    }
    // A nova mensagem também altera o total de conversas não lidas da caixa.
    // A reconciliação é agrupada para não iniciar uma consulta pesada por
    // mensagem e não competir com a entrega do próprio evento.
    scheduleRealtimeStatsRefresh();
  }
);

useEmitter(
  'conversation_realtime_read',
  ({ conversationId, conversation: conversationPayload } = {}) => {
    const normalizedConversationId = Number(conversationId);
    if (!normalizedConversationId) return;

    const conversation =
      getConversationById.value(normalizedConversationId) || conversationPayload;
    const previousUnreadCount = Number(conversation?.unread_count || 0);
    if (!previousUnreadCount) return;

    // O servidor ainda reconcilia o snapshot abaixo, mas a leitura já é
    // refletida no badge no mesmo ciclo do evento do WebSocket.
    adjustUnreadCountOptimistically({
      conversationId: normalizedConversationId,
      previousUnreadCount,
      unreadCount: 0,
      conversation,
    });
  }
);

useEmitter(BUS_EVENTS.WEBSOCKET_RECONNECT_COMPLETED, () => {
  refreshConversationListInBackground({ force: true });
});

// O merge otimista de mensagens realtime nesta cache (session storage) roda
// fora do Vuex e pode divergir do que o backend consideraria visível para o
// agente (Aceleres::ConversationHistoryScope). Quando o store reconcilia a
// preview com a versão autoritativa (store/modules/conversations/actions.js
// #reconcileConversationPreview), aplicamos a mesma correção aqui para que a
// cache de sessão não reintroduza o preview desatualizado ao trocar de aba.
useEmitter(
  BUS_EVENTS.ACELERES_CONVERSATION_PREVIEW_RECONCILED,
  conversation => {
    synchronizeConversationInAceleresCaches(conversation);
  }
);

useEmitter(
  BUS_EVENTS.ACELERES_CONVERSATION_STARTED,
  ({ conversationId } = {}) => {
    showMineTabAfterConversationStart({ conversationId });
  }
);

// Rede de seguranca. Todo o tempo real da lista depende de eventos pontuais;
// se um deles for descartado (aba oculta, filtro, corrida), nada mais pediria a
// lista de novo e a conversa ficava fora por tempo indeterminado. Este ciclo
// limita qualquer buraco futuro a um minuto -- e a volta da aba ao primeiro
// plano dispara uma vez na hora, porque enquanto oculta o ciclo nao roda.
let chatListSafetyRefreshTimer = null;

function runChatListSafetyRefresh({ refreshStats = false } = {}) {
  refreshConversationListInBackground({
    force: false,
    refreshStats,
  }).catch(() => {});
}

function onDocumentVisibilityChange() {
  if (document.hidden) return;
  // A aba suspensa pode perder eventos do WebSocket. A lista é reconciliada
  // pelo refresh incremental, e os contadores precisam de uma leitura própria
  // para não depender de um evento que nunca chegou ao navegador.
  runChatListSafetyRefresh({ refreshStats: true });
}

onMounted(() => {
  // A rota pode iniciar em Meus. Apenas sincroniza o estado aqui; a carga
  // única da lista/contadores acontece em resetAndFetchData abaixo.
  syncAssigneeTabFromRoute({ fetchData: false });
  store.dispatch('setChatListFilters', conversationFilters.value);
  setFiltersFromUISettings();
  store.dispatch('setChatStatusFilter', activeStatus.value);
  store.dispatch('setChatSortFilter', activeSortBy.value);
  // Os dados dos menus entram na mesma barreira curta da lista e contadores.
  ensureAceleresCriticalMenuData();
  // Aceleres: ao entrar no Zoraxy, a lista deve abrir nas caixas globais
  // (Novos/Meus) sem reaplicar filtro salvo de uma sessao anterior. Filtro salvo
  // podia manter inbox/condicoes antigas e zerar os badges mesmo com conversas.
  aceleresDropdownFilterRef.value?.clearAll(false);
  resetAndFetchData({ bypassCache: true });
  if (hasActiveFolders.value) {
    store.dispatch('campaigns/get');
  }
  emitter.on(BUS_EVENTS.MESSAGE_SENT, closeQuickSearch);
  emitter.on(
    BUS_EVENTS.ACELERES_OPEN_CHAT_WITH_PHONE,
    openChatWithPhoneFromBus
  );
  emitter.on(BUS_EVENTS.ACELERES_MOVE_TO_MINE_REQUESTED, onMoveToMineRequested);
  window.addEventListener(
    EMPTY_STATE_NEW_CONTACT_EVENT,
    openNewContactFromEmptyState
  );
  document.addEventListener('visibilitychange', onDocumentVisibilityChange);
  chatListSafetyRefreshTimer = setInterval(
    runChatListSafetyRefresh,
    CHAT_LIST_AUTO_REFRESH_INTERVAL
  );
  // Aceleres: funil abre conversa com ?qs=NomeDaContato -> auto-popula busca
  const qsParam = route.query.qs;
  if (qsParam && qsParam.trim().length >= 2) {
    quickSearch.value = qsParam.trim();
    onQuickSearchInput();
    const newQuery = Object.assign({}, route.query);
    delete newQuery.qs;
    router.replace({ query: newQuery });
  }
});

onUnmounted(() => {
  emitter.off(BUS_EVENTS.MESSAGE_SENT, closeQuickSearch);
  emitter.off(
    BUS_EVENTS.ACELERES_MOVE_TO_MINE_REQUESTED,
    onMoveToMineRequested
  );
  emitter.off(
    BUS_EVENTS.ACELERES_OPEN_CHAT_WITH_PHONE,
    openChatWithPhoneFromBus
  );
  window.removeEventListener(
    EMPTY_STATE_NEW_CONTACT_EVENT,
    openNewContactFromEmptyState
  );
  document.removeEventListener('visibilitychange', onDocumentVisibilityChange);
  autoRefreshQueued = false;
  if (realtimeStatsRefreshTimer) {
    clearTimeout(realtimeStatsRefreshTimer);
    realtimeStatsRefreshTimer = null;
  }
  if (realtimeConversationRefreshTimer) {
    clearTimeout(realtimeConversationRefreshTimer);
    realtimeConversationRefreshTimer = null;
  }
  if (filteredRealtimeRefreshTimer) {
    clearTimeout(filteredRealtimeRefreshTimer);
    filteredRealtimeRefreshTimer = null;
  }
  if (chatListSafetyRefreshTimer) {
    clearInterval(chatListSafetyRefreshTimer);
    chatListSafetyRefreshTimer = null;
  }
});

const selectedConversationId = ref(null);
const showDeleteConversationPopup = ref(false);

// Aceleres 2026-08-28: o modal foi extraido para AclConversationDeleteModal.vue.
// O pai guarda apenas qual conversa esta selecionada e reage ao resultado --
// o estado de "excluindo", os textos e a chamada da store vivem no filho.
const selectedConversationForDelete = computed(() =>
  conversationList.value.find(
    conversation =>
      Number(conversation?.id) === Number(selectedConversationId.value)
  )
);

function closeDeleteConversationModal() {
  showDeleteConversationPopup.value = false;
  selectedConversationId.value = null;
}

function onConversationDeleted() {
  redirectToConversationList();
  closeDeleteConversationModal();
}

const handleDelete = conversationId => {
  selectedConversationId.value = conversationId;
  showDeleteConversationPopup.value = true;
};

// Aceleres: modo seleção (estilo WhatsApp) — disparado pelo menu 3-dots
// no ChatListHeader; substitui o hover-to-select que ficava no avatar.
const selectionMode = ref(false);
const enterSelectionMode = () => {
  // A barra de busca some junto com as abas; deixar uma busca aberta atras
  // dela mostraria resultados sem campo nenhum pra limpar.
  closeQuickSearch();
  selectionMode.value = true;
};
const exitSelectionMode = () => {
  selectionMode.value = false;
  resetBulkActions();
};

const bulkMarkUnread = async () => {
  const ids = [...selectedConversations.value];
  if (!ids.length) return;
  await Promise.all(
    ids.map(id => store.dispatch('markMessagesUnread', { id }).catch(() => {}))
  );
  resetBulkActions();
  selectionMode.value = false;
};
const bulkMute = async () => {
  const ids = [...selectedConversations.value];
  if (!ids.length) return;
  await Promise.all(
    ids.map(id => store.dispatch('muteConversation', id).catch(() => {}))
  );
  resetBulkActions();
  selectionMode.value = false;
};
const bulkResolve = async () => {
  const count = selectedConversations.value.length;
  if (!count) return;
  // Aceleres: o modal do tipo de conclusão (dentro de onUpdateConversations)
  // substitui o window.confirm; cancelar não conclui e mantém a seleção.
  const concluded = await onUpdateConversations(
    wootConstants.STATUS_TYPE.RESOLVED,
    null
  );
  if (concluded === false) return;
  selectionMode.value = false;
};
provide('selectConversation', selectConversation);
provide('deSelectConversation', deSelectConversation);
provide('assignAgent', onAssignAgent);
provide('assignSelf', onAssignSelf);
provide('startConversation', onStartConversation);
provide('assignMine', onAssignMine);
provide('assignPersonal', onAssignPersonal);
provide('assignTeam', onAssignTeam);
provide('assignLabels', onAssignLabels);
provide('removeLabels', onRemoveLabels);
provide('updateConversationStatus', handleResolveConversation);
provide('toggleContextMenu', onContextMenuToggle);
provide('markAsUnread', markAsUnread);
provide('markAsRead', markAsRead);
provide('assignPriority', assignPriority);
provide('togglePin', togglePin);
provide('isConversationSelected', isConversationSelected);
provide('deleteConversation', handleDelete);
provide('aclSelectionMode', selectionMode);

watch(
  () => route.query[ACELERES_BOX_QUERY_PARAM],
  () => syncAssigneeTabFromRoute({ immediateStats: true })
);

watch(shouldMergeArchivedIntoConcluded, isMobileViewport => {
  if (!isMobileViewport) {
    return;
  }

  if (activeAssigneeTab.value === ARCHIVED_ASSIGNEE_TAB) {
    updateAssigneeTab(CONCLUDED_ASSIGNEE_TAB);
    return;
  }

  if (
    normalizeAceleresBoxQuery(route.query[ACELERES_BOX_QUERY_PARAM]) ===
    'arquivadas'
  ) {
    setAceleresBoxQuery(CONCLUDED_ASSIGNEE_TAB);
  }
});

watch(activeTeam, () => resetAndFetchData());

// Aceleres: caso o papel do usuario so fique disponivel apos a montagem
// (ex.: refresh com store repopulado de forma assincrona), reaplica o
// default correto da aba enquanto o usuario nao escolheu uma manualmente.
watch(
  () => store.getters.getCurrentRole,
  role => {
    if (aceleresUserPickedTab.value) return;
    // Aceleres 4 caixas: todos (gestor e vendedor) abrem em NOVOS. Antes isso
    // forçava 'all' p/ gestor, que não existe mais nos items → activeAssigneeTabCount
    // achava undefined e estourava, quebrando a paginação/conversas.
    const desired = 'novos';
    if (activeAssigneeTab.value !== desired) {
      activeAssigneeTab.value = desired;
      resetAndFetchData();
    }
  }
);

watch(
  computed(() => props.conversationInbox),
  () => resetAndFetchData()
);
watch(
  computed(() => props.label),
  () => resetAndFetchData()
);
watch(
  computed(() => props.conversationType),
  () => resetAndFetchData()
);

watch(activeFolder, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('customViews/setActiveConversationFolder', newVal || null);
  }
  resetAndFetchData();
});

watch(chatLists, () => {
  chatsOnView.value = conversationList.value;
});

watch(conversationStatusSnapshot, () => {
  (allConversationsList.value || [])
    .filter(
      conversation =>
        conversation?.status === wootConstants.STATUS_TYPE.RESOLVED
    )
    .forEach(synchronizeConversationInAceleresCaches);

  chatsOnView.value = conversationList.value;
  cacheCurrentAceleresConversationList();
});

watch(conversationFilters, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('updateChatListFilters', newVal);
  }
});

// === Aceleres: largura redimensionável da lista de conversas ===
// Aceleres 2026-08-28: a largura arrastavel virou composable.
// Ver dashboard/composables/useAclListResize.js -- o onUnmounted que limpa os
// listeners de window foi junto, senao a coluna redimensiona sozinha ao trocar de rota.
const { aclListWidth, isAclResizing, onAclResizeStart, onAclResizeReset } =
  useAclListResize();
</script>

<template>
  <div
    class="flex flex-col flex-shrink-0 conversations-list-wrap conversas bg-n-surface-1 acl-conv-list-resizable relative"
    :class="[
      { hidden: !showConversationList },
      isOnExpandedLayout ? 'basis-full' : '',
    ]"
    :style="!isOnExpandedLayout ? { width: aclListWidth + 'px' } : null"
  >
    <slot />
    <StarredMessagesPanel
      v-if="showStarredMessages"
      @close="closeStarredMessages"
      @open-message="openStarredMessage"
    />
    <ChatListHeader
      v-if="!showStarredMessages"
      :page-title="pageTitle"
      :has-applied-filters="hasAppliedFiltersForUI"
      :has-active-folders="hasActiveFolders"
      :active-assignee-tab="activeAssigneeTab"
      :is-on-expanded-layout="isOnExpandedLayout"
      :conversation-stats="conversationStats"
      :is-list-loading="
        shouldShowChatListLoadingIndicator && !conversationList.length
      "
      :is-floating-header="isDashboardHomeRoute"
      :funnels="funnels"
      :selected-funnel-id="selectedFunnelId"
      @add-folders="onClickOpenAddFoldersModal"
      @delete-folders="onClickOpenDeleteFoldersModal"
      @filters-modal="onToggleAdvanceFiltersModal"
      @reset-filters="resetAndFetchData"
      @basic-filter-change="onBasicFilterChange"
      @enter-selection-mode="enterSelectionMode"
      @open-starred-messages="openStarredMessages"
      @open-archived="toggleConcludedTab"
      @new-conversation="openHeaderNewConversation"
      @select-funnel="onSelectFunnel"
    />
    <AceleresNewConversationPanel
      v-if="!showStarredMessages"
      :show="showNewConversationPanel"
      :account-id="accountIdForQuickSearch"
      :teleport-target="newConversationPanelTeleportTarget"
      overlay-above-header
      :close-on-new-contact-back="showNewContactInEmptyState"
      @close="closeHeaderNewConversation"
      @start-phone-conversation="startConversationFromPanel"
      @open-contact-chat="openContactChatFromPanel"
      @open-existing-phone="startConversationFromPanel"
      @edit-contact="editContactFromPanel"
    />
    <FunnelConfigurationModal
      v-if="showFunnelConfigurationModal"
      v-model:show="showFunnelConfigurationModal"
      :account-id="currentAccountId"
      :funnel="funnels.find(item => String(item.id) === String(selectedFunnelId)) || funnels[0]"
      @edit-stage="openStageConfiguration"
      @open-assistant="showFunnelAssistantModal = true"
      @saved="refreshFunilStages"
    />
    <FunnelAssistantModal
      v-if="showFunnelAssistantModal"
      v-model:show="showFunnelAssistantModal"
      :account-id="currentAccountId"
      @applied="refreshFunilStages"
    />
    <FunnelStageConfigurationModal
      v-if="showFunnelStageConfigurationModal"
      v-model:show="showFunnelStageConfigurationModal"
      :account-id="currentAccountId"
      :stage="selectedStageForConfiguration"
      @saved="refreshFunilStages"
    />
    <AceleresStartConversationFlow
      ref="startConversationFlowRef"
      :account-id="accountIdForQuickSearch"
      @started="onStartConversationFlowStarted"
    />
    <div
      v-show="!showStarredMessages"
      class="acl-dashboard-flow flex-1 min-h-0 flex flex-col"
      :class="{
        'acl-dashboard-flow--with-hero': isDashboardHomeRoute,
      }"
    >
      <TeleportWithDirection
        v-if="showAddFoldersModal"
        to="#saveFilterTeleportTarget"
      >
        <SaveCustomView
          v-model="appliedFilter"
          :custom-views-query="foldersQuery"
          :open-last-saved-item="openLastSavedItemInFolder"
          @close="onCloseAddFoldersModal"
        />
      </TeleportWithDirection>

      <DeleteCustomViews
        v-if="showDeleteFoldersModal"
        v-model:show="showDeleteFoldersModal"
        :active-custom-view="activeFolder"
        :custom-views-id="foldersId"
        :open-last-item-after-delete="openLastItemAfterDeleteInFolder"
        @close="onCloseDeleteFoldersModal"
      />

      <!-- Aceleres: no modo seleção a toolbar toma o lugar da busca e dos
           filtros, encostada no .acl-chatlist-header. Fica AQUI, no topo do
           fluxo, e não junto da lista: mais abaixo ela nasceria depois da
           barra de filtros, do atalho de Concluídas e do empty state.
           ConversationBulkActions nativo segue desabilitado em favor dela;
           selectedConversations (vuex bulkActions) continua compartilhado
           pra reuso dos endpoints. -->
      <AclSelectionToolbar
        v-if="selectionMode"
        :count="selectedConversations.length"
        @exit="exitSelectionMode"
        @mark-unread="bulkMarkUnread"
        @mute="bulkMute"
        @resolve="bulkResolve"
      />

      <!-- Aceleres: barra de busca rápida com dropdown live -->
      <div class="flex items-center px-3.5 md:block md:p-0">
        <div
          v-if="!selectionMode"
          class="acl-quick-search-wrap relative flex-1 min-w-0"
        >
          <SearchPill
            v-model="quickSearch"
            placeholder="Buscar nome ou telefone"
            clear-label="Limpar busca"
            @input="onQuickSearchInput"
            @enter="onQuickSearchEnter"
            @focus="quickSearchOpen = quickSearch.length >= 1"
            @clear="closeQuickSearch"
          />
        </div>
      </div>

      <!-- Aceleres: resultados da busca inline na coluna (estilo WhatsApp) -->
      <div
        v-if="isSearchActive"
        class="flex-1 min-h-0 overflow-y-auto overflow-x-hidden pb-2"
      >
        <div
          v-if="quickSearchLoading"
          class="px-3 py-3 text-xs text-n-slate-11"
        >
          Buscando…
        </div>

        <div
          v-else-if="quickSearchError"
          class="flex flex-col items-center gap-3 px-3 py-6 text-center text-xs text-n-slate-11"
        >
          <span>{{ t('CHAT_LIST.QUICK_SEARCH.ERROR') }}</span>
          <button
            type="button"
            class="rounded-lg border border-n-weak px-3 py-2 font-semibold text-n-slate-12 hover:bg-n-alpha-2"
            @click="onQuickSearchEnter"
          >
            {{ t('CHAT_LIST.QUICK_SEARCH.RETRY') }}
          </button>
        </div>

        <div v-if="quickSearchConversations.length" class="px-1 py-1">
          <div class="px-3 py-1 text-[11px] font-semibold text-n-slate-11">
            {{ quickSearchConversationHeading }}
          </div>
          <button
            v-for="cv in quickSearchConversations.slice(0, 20)"
            :key="'cv' + cv.id"
            @click="openConvFromQuick(cv)"
            class="w-full text-left px-2 py-2 rounded-lg flex items-center gap-3 transition-colors"
            :class="{
              'active bg-n-background': isQuickSearchConversationActive(cv.id),
              'hover:bg-n-alpha-2': !isQuickSearchConversationActive(cv.id),
            }"
          >
            <Avatar
              :name="cv.contact?.name || cv.contact?.phone_number || '?'"
              :src="cv.contact?.thumbnail"
              :size="40"
              rounded-full
            />
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium text-n-slate-12 truncate">
                {{
                  cv.contact?.name ||
                  cv.contact?.phone_number ||
                  '(sem contato)'
                }}
              </div>
              <div class="text-xs text-n-slate-10 truncate">
                {{ cv.contact?.phone_number || '' }}
              </div>
              <div
                class="text-xs text-n-slate-10 truncate"
                :title="quickSearchConversationMeta(cv)"
              >
                {{ quickSearchConversationMeta(cv) }}
              </div>
              <div
                v-if="cv.message?.content"
                class="text-xs text-n-slate-10 truncate"
              >
                {{ cv.message.content }}
              </div>
            </div>
          </button>
        </div>

        <div
          v-if="
            quickSearchConversations.length &&
            quickSearchContacts.length
          "
          class="my-1 mx-3 h-px bg-n-weak"
        ></div>

        <div v-if="quickSearchContacts.length" class="px-1 py-1">
          <div class="px-3 py-1 text-[11px] font-semibold text-n-slate-11">
            Contatos
          </div>
          <button
            v-for="ct in quickSearchContacts.slice(0, 20)"
            :key="'c' + ct.id"
            @click="openContactFromQuick(ct)"
            class="w-full text-left px-2 py-2 rounded-lg hover:bg-n-alpha-2 flex items-center gap-3"
          >
            <Avatar
              :name="ct.name || ct.phone_number || '?'"
              :src="ct.thumbnail"
              :size="40"
              rounded-full
            />
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium text-n-slate-12 truncate">
                {{ ct.name || ct.phone_number }}
              </div>
              <div class="text-xs text-n-slate-10 truncate">
                {{ ct.phone_number }}
              </div>
            </div>
          </button>
        </div>

        <div
          v-if="quickSearchNoResults && quickSearchIsPhone"
          class="px-3 pt-2"
        >
          <button
            :disabled="isStartingConversation"
            class="w-full px-3 py-2.5 bg-n-brand text-white rounded-lg text-xs font-semibold hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
            @click="openNewConversationForPhone()"
          >
            <span class="i-lucide-message-circle-plus size-3.5" />
            {{
              isStartingConversation
                ? 'Abrindo...'
                : 'Iniciar nova conversa com ' + quickSearch
            }}
          </button>
        </div>

        <div
          v-else-if="quickSearchNoResults"
          class="px-3 py-6 text-center text-xs text-n-slate-11"
        >
          Nenhum resultado.
        </div>
      </div>

      <div v-show="!isSearchActive" class="flex-1 min-h-0 flex flex-col">
        <div class="acl-filters-bar">
          <ChatTypeTabs
            v-if="!hasAppliedFiltersOrActiveFoldersForUI && !selectionMode"
            :items="assigneeTabItems"
            :active-tab="activeAssigneeTab"
            :active-filter-count="aceleresFilterCount"
            :is-filter-menu-open="aceleresFilterMenuOpen"
            is-compact
            @chat-tab-change="updateAssigneeTab"
            @sort-change="onSortChange"
            @close-filter-menu="aceleresDropdownFilterRef?.closeModal()"
            @filter-click="
              anchorEl =>
                aceleresDropdownFilterRef?.openModal('popover', anchorEl)
            "
          />
          <div
            class="acl-mobile-filter-row"
            :class="{ 'acl-mobile-filter-row--collapsed': selectionMode }"
          >
            <Transition name="acl-unread-subfilters-fade">
              <div
                v-if="shouldShowUnreadSubfilters && !selectionMode"
                class="acl-unread-subfilters !ps-5 max-md:!ps-0"
              >
                <button
                  type="button"
                  class="acl-unread-subfilter-btn max-md:!min-h-[30px]"
                  :class="{
                    'acl-unread-subfilter-btn--active':
                      activeUnreadSubfilter === UNREAD_SUBFILTER_ALL,
                  }"
                  :aria-pressed="
                    activeUnreadSubfilter === UNREAD_SUBFILTER_ALL
                  "
                  @click="updateUnreadSubfilter(UNREAD_SUBFILTER_ALL)"
                >
                  Todos
                </button>
                <button
                  type="button"
                  class="acl-unread-subfilter-btn acl-unread-subfilter-btn--unread max-md:!min-h-[30px]"
                  :class="{
                    'acl-unread-subfilter-btn--active':
                      activeUnreadSubfilter === UNREAD_SUBFILTER_ONLY,
                  }"
                  :aria-pressed="
                    activeUnreadSubfilter === UNREAD_SUBFILTER_ONLY
                  "
                  @click="updateUnreadSubfilter(UNREAD_SUBFILTER_ONLY)"
                >
                  <span>Não lidos</span>
                </button>
              </div>
            </Transition>
            <div
              id="acl-mobile-conversation-actions"
              class="acl-mobile-filter-actions md:hidden"
            />
          </div>
          <AceleresDropdownFilter
            v-if="!hasActiveFolders"
            ref="aceleresDropdownFilterRef"
            :inboxes="inboxesList"
            :labels="labels"
            :agents="agentList"
            :teams="teamsList"
            :show-inbox-filter="isManagerRole"
            @apply="onAceleresFilterApply"
            @clear="onAceleresFilterClear"
            @count-change="aceleresFilterCount = $event"
            @open-state-change="aceleresFilterMenuOpen = $event"
          />
        </div>

        <button
          v-if="!hasAppliedFiltersOrActiveFoldersForUI"
          type="button"
          class="acl-archived-row"
          :class="{
            'acl-archived-row--active':
              activeAssigneeTab === CONCLUDED_ASSIGNEE_TAB,
          }"
          @click="toggleConcludedTab"
        >
          <span class="acl-archived-row__icon" aria-hidden="true">
            <span class="i-lucide-archive" />
          </span>
          <span class="acl-archived-row__label">{{ CONCLUDED_TAB_LABEL }}</span>
        </button>

        <!-- Aceleres: empty state contextual. Quando o filtro "Atendidos pela IA"
         está ativo e a lista vem vazia (sem resultados OU erro 422 absorvido
         pela action), mostramos mensagem específica em vez do 404 genérico. -->
        <AclChatListEmptyState
          :visible="!shouldShowChatListLoadingIndicator && !conversationList.length"
          :has-error="chatListHasError"
          :ia-filter-applied="appliedIaFilter"
          :label-message="labelFilterEmptyMessage"
          @retry="retryFetchConversations"
        />
        <div
          ref="conversationListRef"
          class="flex-1 min-h-0 overflow-y-auto conversations-list mt-5"
          :class="{ '!overflow-hidden': isContextMenuOpen }"
          @scroll.passive="onConversationListScroll"
        >
          <Virtualizer
            ref="virtualListRef"
            v-slot="{ item, index }"
            :data="conversationList"
            :buffer-size="ACELERES_VIRTUAL_BUFFER_PX"
            :start-margin="virtualListStartMargin"
          >
            <ConversationItem
              :key="item.id"
              :source="item"
              :label="activeConversationLabel"
              :team-id="teamId"
              :folders-id="foldersId"
              :conversation-type="conversationType"
              :active-assignee-tab="activeAssigneeTab"
              :show-assignee="showAssigneeInConversationCard"
              :data-index="index"
              @select-conversation="selectConversation"
              @de-select-conversation="deSelectConversation"
            />
          </Virtualizer>
          <!-- A borda de cima mede exactamente o cartao de conversa, nem a
               coluna inteira nem so o texto. O cartao nao e filho directo
               desta coluna: a linha do virtualizador que o embrulha tem
               `padding: 0 10px 0 17px` (medido no browser), e e dai que vem
               a diferenca. Como este paragrafo E filho directo, as mesmas
               medidas entram como margem — `width: auto` faz o resto, e a
               caixa fica com a largura e o x do cartao.
               Se um dia esse recuo da lista mudar, este tem de acompanhar. -->
          <p
            v-if="showEndOfListMessage"
            class="ltr:ml-[17px] ltr:mr-[10px] rtl:ml-[10px] rtl:mr-[17px] mt-3 border-t border-n-weak px-3 pb-4 pt-3 text-center text-n-slate-11"
          >
            {{ endOfListMessage }}
          </p>
          <IntersectionObserver
            v-else
            :options="intersectionObserverOptions"
            @observed="loadMoreConversations"
          />
        </div>
        <div
          v-if="shouldShowChatListLoadingIndicator"
          class="flex shrink-0 justify-center bg-n-surface-1 py-4"
        >
          <Spinner class="text-n-brand" />
        </div>
      </div>
      <AclConversationDeleteModal
        :open="showDeleteConversationPopup"
        :conversation="selectedConversationForDelete"
        @close="closeDeleteConversationModal"
        @deleted="onConversationDeleted"
      />
      <AclPinLimitModal
        :open="showPinLimitModal"
        @close="showPinLimitModal = false"
      />
      <TeleportWithDirection
        v-if="showAdvancedFilters"
        to="#conversationFilterTeleportTarget"
      >
        <ConversationFilter
          v-if="hasActiveFolders"
          v-model="appliedFilter"
          :folder-name="activeFolderName"
          is-folder-view
          @apply-filter="onApplyFilter"
          @update-folder="onUpdateSavedFilter"
          @close="closeAdvanceFiltersModal"
        />
        <QuickConversationFilter
          v-else
          @apply-filter="onApplyFilter"
          @close="closeAdvanceFiltersModal"
        />
      </TeleportWithDirection>
      <ConversationResolveAttributesModal
        ref="resolveAttributesModalRef"
        @submit="handleResolveWithAttributes"
      />
    </div>
    <div
      v-if="!isOnExpandedLayout"
      class="acl-conv-list-resize-handle"
      :class="{ 'acl-conv-list-resize-handle--active': isAclResizing }"
      @mousedown.prevent="onAclResizeStart"
      @dblclick="onAclResizeReset"
    />
  </div>
</template>

<style scoped>

.acl-dashboard-flow {
  position: relative;
}

.acl-dashboard-flow--with-hero {
  padding-top: 0;
}

@media (max-width: 767px) {
  .acl-dashboard-flow,
  .acl-dashboard-flow--with-hero {
    padding-top: calc(96px + env(safe-area-inset-top, 0px));
  }
}

.acl-filters-bar {
  display: flex;
  flex-direction: column;
  min-width: 0;
  border-bottom: 0 !important;
  box-shadow: none !important;
}

@media (min-width: 768px) {
  .acl-filters-bar :deep(.acl-pill-tabs) {
    border-bottom: 0 !important;
    box-shadow: none !important;
  }
}
.acl-mobile-filter-row {
  display: contents;
}
/* Aceleres: no modo selecao as abas e os subfiltros saem do ar. Sem esta
   regra a linha continuaria reservando os 52px que o breakpoint mobile lhe
   da, e a toolbar nao encostaria no .acl-chatlist-header. Duas classes
   porque a regra mobile vem depois neste mesmo arquivo. */
.acl-mobile-filter-row.acl-mobile-filter-row--collapsed {
  display: none;
}
.acl-mobile-filter-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: flex-end;
}

.acl-unread-subfilters {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px 6px 28px;
  overflow-x: auto;
  overflow-y: hidden;
  border-bottom: 0 !important;
  box-shadow: none !important;
  scrollbar-width: none;
}
.conversations-list-wrap .acl-filters-bar,
.conversations-list-wrap .acl-unread-subfilters {
  border: 0 !important;
  border-block-end: 0 !important;
  outline: 0 !important;
  box-shadow: none !important;
}
.conversations-list-wrap.conversas.acl-conv-list-resizable .acl-filters-bar {
  border-bottom: none !important;
}
.conversations-list-wrap .acl-filters-bar::before,
.conversations-list-wrap .acl-filters-bar::after,
.conversations-list-wrap .acl-unread-subfilters::before,
.conversations-list-wrap .acl-unread-subfilters::after {
  display: none !important;
  content: none !important;
}
.acl-unread-subfilters::-webkit-scrollbar {
  display: none;
}
.acl-unread-subfilter-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex-shrink: 0;
  min-height: 30px;
  padding: 7px 12px;
  border: 1px solid #d4d4d4;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  color: #767676;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  transition:
    background-color 0.12s,
    color 0.12s,
    border-color 0.12s,
    box-shadow 0.12s;
}
.acl-unread-subfilter-btn:hover {
  color: #525252;
  border-color: #b5b5b5;
  background: rgba(255, 255, 255, 0.92);
}
.acl-unread-subfilter-btn--active,
.acl-unread-subfilter-btn--active:hover {
  background: #d9fdd3;
  color: #15603e;
  border-color: #15603e;
  box-shadow: 0 0 0 1px rgba(21, 96, 62, 0.06);
}
.acl-unread-subfilter-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: #25d366;
  color: #ffffff;
  font-size: 10px;
  font-weight: 800;
  line-height: 18px;
}
.acl-unread-subfilters-fade-enter-active,
.acl-unread-subfilters-fade-leave-active {
  transition:
    opacity 0.16s ease,
    transform 0.16s ease;
}
.acl-unread-subfilters-fade-enter-from,
.acl-unread-subfilters-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
.acl-archived-row {
  display: none;
}
:global(.dark) .acl-unread-subfilter-btn {
  background: rgba(17, 19, 21, 0.72);
  border-color: #3f3f46;
  color: #a1a1aa;
}
:global(.dark) .acl-unread-subfilter-btn:hover {
  background: rgba(24, 24, 27, 0.92);
  border-color: #52525b;
  color: #d4d4d8;
}
:global(.dark) .acl-unread-subfilter-btn--active,
:global(.dark) .acl-unread-subfilter-btn--active:hover {
  background: rgba(21, 96, 62, 0.25);
  border-color: #15603e;
  color: #86efac;
}
.acl-conv-list-resizable {
  position: relative;
  min-height: 0;
  overflow: hidden;
}
.acl-conv-list-resize-handle {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 4px;
  cursor: col-resize;
  z-index: 50;
  background: transparent;
  transition: background-color 0.12s;
}
.acl-conv-list-resize-handle:hover,
.acl-conv-list-resize-handle--active {
  background: rgba(100, 116, 139, 0.35);
}
.acl-quick-search-wrap {
  padding: 6px 0;
}

@media (max-width: 767px) {
  .acl-quick-search-wrap {
    margin-block: 8px 6px;
    padding: 7px 0 5px;
  }

  .acl-mobile-filter-row {
    display: flex;
    min-width: 0;
    min-height: 38px;
    align-items: center;
    gap: 12px;
    padding: 4px 14px;
  }

  .acl-unread-subfilters {
    flex: 1 1 auto;
    min-width: 0;
    gap: 10px;
    padding: 0;
    transform: translateY(5px);
  }

  .acl-unread-subfilter-btn {
    height: 30px;
    padding: 6px 13px;
    font-size: 11px;
  }

  .acl-mobile-filter-actions {
    gap: 10px;
  }

  .acl-archived-row {
    display: flex;
    align-items: center;
    width: 100%;
    min-height: 72px;
    margin-top: 4px;
    gap: 14px;
    padding: 10px 14px;
    border: 0;
    border-bottom: 1px solid #eef0f2;
    background: transparent;
    color: #54656f;
    text-align: left;
  }

  .acl-archived-row--active {
    background: rgba(17, 27, 33, 0.04);
    color: #0b8457;
  }

  .acl-archived-row__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 30px;
    height: 30px;
    color: inherit;
  }

  .acl-archived-row__icon > span {
    width: 22px;
    height: 22px;
    background-color: currentColor;
  }

  .acl-archived-row__label {
    flex: 1 1 auto;
    min-width: 0;
    color: #111b21;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.2;
  }

  .acl-archived-row--active .acl-archived-row__label {
    color: #0b8457;
  }

  .acl-archived-row__count {
    flex: 0 0 auto;
    color: #0b8457;
    font-size: 12px;
    font-weight: 700;
  }

  :global(.dark) .acl-archived-row {
    color: #8696a0;
  }

  :global(.dark) .acl-archived-row--active {
    background: rgba(233, 237, 239, 0.05);
    color: #25d366;
  }

  :global(.dark) .acl-archived-row__label {
    color: #e9edef;
  }

  :global(.dark) .acl-archived-row--active .acl-archived-row__label,
  :global(.dark) .acl-archived-row__count {
    color: #25d366;
  }

  .conversations-list {
    margin-top: 0 !important;
    padding-bottom: 0;
    scroll-padding-bottom: calc(78px + env(safe-area-inset-bottom, 0px));
  }

  .conversations-list::after {
    content: '';
    display: block;
    height: calc(78px + env(safe-area-inset-bottom, 0px));
  }
}
</style>
